import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

// ─── קבועים ──────────────────────────────────────────────────────────────────

const ICE_CREAM_TYPES = [
  { id:"dairy",        label:"🥛 חלבי",         color:"#F5E6CA", accent:"#D4A855" },
  { id:"heavyCream",   label:"🧈 שמנת כבדה",    color:"#FFF0D0", accent:"#C07A20" },
  { id:"coconut",      label:"🥥 קוקוס",        color:"#D4EDD4", accent:"#2D8A4E" },
  { id:"fruitCoconut", label:"🍓 פרי & קוקוס",  color:"#FFE8F0", accent:"#D4507A" },
  { id:"sorbet",       label:"🍧 סורבה",        color:"#FFD6D6", accent:"#E8394D" },
  { id:"protein",      label:"💪חלבון",        color:"#E8F4FF", accent:"#0EA5E9" },
  { id:"vegan",        label:"🌱 טבעוני",       color:"#E0F0FF", accent:"#2563EB" },
  { id:"cocktail",     label:"🍹 קוקטייל",      color:"#F0D6FF", accent:"#7C3AED" },
  { id:"mixed",        label:"🎨 מעורב",        color:"#F0F0F0", accent:"#888",
    desc:"חישוב לפי המרכיב הדומיננטי" },
];

const TARGETS = {
  heavyCream:   { fat:[10,14], sugar:[14,19], water:[55,66], protein:[3,6]  },
  dairy:        { fat:[4,9],   sugar:[16,22], water:[62,74], protein:[4,7]  },
  coconut:      { fat:[8,13],  sugar:[15,21], water:[58,70], protein:[1,3]  },
  fruitCoconut: { fat:[5,10],  sugar:[18,26], water:[65,78], protein:[1,3]  },
  sorbet:       { fat:[0,2],   sugar:[22,32], water:[67,80], protein:[0,1]  },
  protein:      { fat:[3,9],   sugar:[8,16],  water:[60,75], protein:[8,16] },
  vegan:        { fat:[5,12],  sugar:[15,24], water:[58,74], protein:[1,6]  },
  cocktail:     { fat:[4,12],  sugar:[12,22], water:[55,72], protein:[1,4]  },
  mixed:        { fat:[7,13],  sugar:[14,21], water:[58,72], protein:[4,10] },
};

const RISK_ZONES = {
  fat:      { danger: 16,  msg: "🧈 שומן גבוה מ-16% – הגלידה עלולה לקבל מרקם חמאתי/שמנוני" },
  alcohol:  { danger: 2,   msg: "🍸 אתנול מעל 2% – הגלידה לא תתקשה כראוי, הפחת אלכוהול" },
  protein:  { danger: 17,  msg: "💪 חלבון מעל 17% – מרקם גירי/אלסטי/פודינגי, הוסף סוכר ומייצבים" },
  water:    { danger: 78,  msg: "💧 מים מעל 78% – סיכון לגבישי קרח, הוסף סוכר או מוצקים" },
  sugarLow: { threshold: 12, msg: "🍭 סוכר נמוך מ-12% – סכנת בלוק קרח, הוסף ממתיק או מייצב נפחי" },
};
// שמן קוקוס טהור – מוגבל ל-1% מסך המתכון (מרקם שעוותי/גרגירי)
const COCONUT_OIL_MAX_PCT = 1;
const CONTAINER_SIZES = [
  { ml:500, label:'500 מ"ל', desc:"רגילה", icon:"🥤", minFill:400, maxFill:500 },
  { ml:750, label:'750 מ"ל', desc:"גדולה", icon:"🍶", minFill:600, maxFill:750 },
];

const SWEETENER_IDS = ["sugar","allulose","honey","silan","maple"];
const ALCOHOL_IDS   = ["baileys","kahlua","amaretto","rum","coconutRum","gin","arak","vodka","tequila"];
const FAT_IDS       = ["coconutOil","almondButter","hazelnutButter","peanutButter","nutButter","pistachioPure","pistachioSweet","cream38"];
const LIQUID_IDS    = ["water","lemonJuice","orangeJuice","watermelonJuice","espresso"];
const FRUIT_IDS     = ["strawberry","banana","peach","pineapple","apple","cinnamonApple","pear","blueberry","raspberry","cranberry","watermelon","mango","lemon","acaiFrozen","acaiPack","acaiPowder"];
const PUDDING_IDS = ["instantPudding"];

const INGREDIENTS = {
  dairy: [
    { id:"milk3",         name:"🥛 חלב 3%",               unit:'מ"ל', fat:3,   sugar:4.8, water:90, protein:3.2 },
    { id:"cream38",       name:"🥛 שמנת מתוקה 38%",        unit:'מ"ל', fat:38,  sugar:3.2, water:56, protein:2.1 },
    { id:"cookingCream",  name:"🥛 שמנת לבישול 15%",       unit:'מ"ל', fat:15,  sugar:3.5, water:79, protein:2.3 },
    { id:"sourCream",     name:"🥛 שמנת חמוצה 18%",            unit:"גרם", fat:18,  sugar:3.6, water:74, protein:2.8 },
    { id:"greekYogurt",   name:"🥛 יוגורט יווני 5%",          unit:"גרם", fat:5,   sugar:3.6, water:81, protein:9   },
    { id:"greekYogurt10", name:"🥛 יוגורט יווני 10%",         unit:"גרם", fat:10,  sugar:3.6, water:76, protein:8   },
    { id:"regularYogurt", name:"🥛 יוגורט רגיל 1.5%",           unit:"גרם", fat:1.5, sugar:4.7, water:87, protein:4   },
    { id:"condensedMilk", name:"🥛 חלב מרוכז ממותק 8.7%",       unit:"גרם", fat:8.7, sugar:54,  water:27, protein:7.9 },
    { id:"almondMilk",    name:"🌾 חלב שקדים 1.1%",             unit:'מ"ל', fat:1.1, sugar:3,   water:94, protein:0.4, note:"לא ממותק" },
    { id:"soyMilk",       name:"🫘 חלב סויה 1.8%",              unit:'מ"ל', fat:1.8, sugar:2.5, water:91, protein:3.3 },
    { id:"hazelnutMilk",  name:"🌰 חלב אגוזי לוז 1.5%",         unit:'מ"ל', fat:1.5, sugar:4,   water:92, protein:0.5 },
    { id:"oatMilk",       name:"🌾 חלב שיבולת שועל 1.5%",       unit:'מ"ל', fat:1.5, sugar:4.5, water:90, protein:1   },
  ],
  coconut: [
    { id:"coconutMilk",  name:"🥥 חלב קוקוס",  unit:'מ"ל', fat:17,  sugar:2.8, water:73, protein:1.6 },
    { id:"coconutCream", name:"🥥 קרם קוקוס",  unit:"גרם", fat:26,  sugar:2.4, water:68, protein:2.3 },
    { id:"coconutOil",   name:"🥥 שמן קוקוס",  unit:"גרם", fat:100, sugar:0,   water:0,  protein:0   },
  ],
  
  proteins: [
    { id:"pro20",     name:"💪 דנונה PRO",           unit:"גרם", fat:1.3, sugar:4.5, water:77, protein:10 },
{ id:"go25",      name:"💪 מעדן GO תנובה",        unit:"גרם", fat:1.5, sugar:4.5, water:75, protein:11 },
{ id:"greek0",    name:"🥛 יוגורט יווני 0%",      unit:"גרם", fat:0,   sugar:4,   water:80, protein:10 },
{ id:"whey",      name:"🥛 אבקת חלבון",           unit:"גרם", fat:5,   sugar:10,  water:6,  protein:75 },
{ id:"proDrink",  name:"🥛 משקה חלבון",           unit:'מ"ל', fat:0.5, sugar:5,   water:85, protein:8  },
{ id:"eggWhite",  name:"🥚 חלבון ביצה נוזלי",     unit:"גרם", fat:0,   sugar:1,   water:88, protein:11 },
{ id:"ricotta",   name:"🧀 ריקוטה 5%",            unit:"גרם", fat:5,   sugar:4.5, water:80, protein:9  },
{ id:"cottage1",  name:"🥛 קוטג' 1%",             unit:"גרם", fat:1,   sugar:3.5, water:82, protein:11 },
{ id:"skyr",      name:"🥛 סקייר טבעי",           unit:"גרם", fat:0.2, sugar:4,   water:82, protein:11 },
{ id:"veganPro",  name:"🌱 מעדן חלבון טבעוני",    unit:"גרם", fat:2.5, sugar:3,   water:80, protein:10 },
  ],
  nuts: [
    { id:"almondButter",   name:"🥜 חמאת שקדים",          unit:"גרם", fat:56, sugar:4.4, water:2,  protein:21 },
    { id:"hazelnutButter", name:"🥜 חמאת אגוזי לוז",      unit:"גרם", fat:60, sugar:5,   water:2,  protein:17 },
    { id:"peanutButter",   name:"🥜 חמאת בוטנים",         unit:"גרם", fat:50, sugar:6,   water:2,  protein:25 },
    { id:"nutButter",      name:"🥜 חמאת אגוזים כללית",   unit:"גרם", fat:58, sugar:5,   water:2,  protein:20 },
    { id:"pistachioPure",  name:"🫘 חמאת פיסטוק 100%",    unit:"גרם", fat:54, sugar:7,   water:2,  protein:21 },
    { id:"pistachioSweet", name:"🫘 חמאת פיסטוק ממותקת",  unit:"גרם", fat:40, sugar:28,  water:3,  protein:15 },
    { id:"tahini",         name:"🫙 טחינה גולמית",         unit:"גרם", fat:54, sugar:0.5, water:3,  protein:25, note:"טחינה 100% ללא תוספות" },
    { id:"halvah",         name:"🍬 חלווה",                unit:"גרם", fat:30, sugar:45,  water:5,  protein:12, note:"חלווה קלאסית עם סוכר" },
  ],
  sweeteners: [
    { id:"sugar",    name:"🍬 סוכר לבן",      unit:"גרם", fat:0,   sugar:100, water:0,  protein:0   },
    { id:"allulose", name:"🔬 אלולוז",         unit:"גרם", fat:0,   sugar:0,   water:0,  protein:0,  note:"0% סוכר אפקטיבי – הוסף 30% יותר" },
    { id:"honey",    name:"🍯 דבש",            unit:"גרם", fat:0,   sugar:82,  water:17, protein:0.3 },
    { id:"silan",    name:"🍯 סילאן",          unit:"גרם", fat:0,   sugar:68,  water:25, protein:2   },
    { id:"maple",    name:"🍁 סירופ מייפל",    unit:"גרם", fat:0.1, sugar:67,  water:32, protein:0   },
    { id:"vanilla",  name:"🌿 תמצית וניל",     unit:'מ"ל', fat:0,   sugar:13,  water:35, protein:0,  note:'כפית = 5מ"ל. מוסיפה ארומה ללא כמעט קלוריות' },
    { id:"xanthan",  name:"🧪 קסנטן גאם",      unit:"גרם", fat:0,   sugar:0,   water:0,  protein:0,  note:"⅛ כפית (0.3גר') ל-500מ\"ל – מרקם חלק. לא ממיס בקר" },
    { id:"guarGum",  name:"🧪 גואר גאם",       unit:"גרם", fat:0,   sugar:0,   water:0,  protein:0,  note:"¼ כפית (0.5גר') ל-500מ\"ל – מרקם קרמי. עדיף מקסנטן לגלידה" },
    { id:"instantPudding", name:"🍮 אינסטנט פודינג", unit:"גרם", fat:2, sugar:55, water:4, protein:2, note:"5–15גר' ל-500מ\"ל. מייצב חזק. פודינג חלבי = לא טבעוני!" },
  ],
  liquids: [
    { id:"water",           name:"💧 מים",              unit:'מ"ל', fat:0,   sugar:0,   water:100, protein:0   },
    { id:"lemonJuice",      name:"🍋 מיץ לימון",        unit:'מ"ל', fat:0.3, sugar:2.5, water:91,  protein:0.4 },
    { id:"orangeJuice",     name:"🍊 מיץ תפוזים",       unit:'מ"ל', fat:0.2, sugar:10,  water:88,  protein:0.7 },
    { id:"watermelonJuice", name:"🍉 מיץ אבטיח",        unit:'מ"ל', fat:0.1, sugar:9,   water:91,  protein:0.5 },
    { id:"espresso",        name:"☕ אספרסו",            unit:'מ"ל', fat:0.2, sugar:0,   water:98,  protein:0.1 },
    { id:"filterCoffee",    name:"☕ קפה פילטר/אמריקן",  unit:'מ"ל', fat:0,   sugar:0,   water:99,  protein:0.1, note:"קפה מבושל – פחות חזק מאספרסו" },
  ],
  alcohol: [
    { id:"baileys",    name:"🍶 ביילי'ס",   unit:'מ"ל', fat:6,   sugar:25, water:52, protein:1.5, abv:17 },
    { id:"kahlua",     name:"🍶 קהלואה",    unit:'מ"ל', fat:0.2, sugar:44, water:42, protein:0.1, abv:20 },
    { id:"amaretto",   name:"🍶 אמרטו",     unit:'מ"ל', fat:0,   sugar:35, water:50, protein:0,   abv:24 },
    { id:"rum",        name:"🥃 רום",       unit:'מ"ל', fat:0,   sugar:0,  water:63, protein:0,   abv:40 },
    { id:"coconutRum", name:"🥥 רום קוקוס", unit:'מ"ל', fat:0,   sugar:12, water:60, protein:0,   abv:21 },
    { id:"gin",        name:"🫙 ג'ין",      unit:'מ"ל', fat:0,   sugar:0,  water:60, protein:0,   abv:40 },
    { id:"arak",       name:"🫙 עראק",      unit:'מ"ל', fat:0,   sugar:0,  water:55, protein:0,   abv:40 },
    { id:"vodka",      name:"🥃 וודקה",     unit:'מ"ל', fat:0,   sugar:0,  water:60, protein:0,   abv:40 },
    { id:"tequila",    name:"🥃 טקילה",     unit:'מ"ל', fat:0,   sugar:0,  water:60, protein:0,   abv:40 },
  ],
  mixins: [
    { id:"chocolate",        name:"🍫 קוביות שוקולד",         unit:"גרם", fat:31,  sugar:52, water:1,  protein:5 },
    { id:"kinderFinger",     name:"🍫 אצבעות קינדר",          unit:"גרם", fat:33,  sugar:51, water:1,  protein:6 },
    { id:"kinderBueno",      name:"🍫 קינדר בואנו לבן",       unit:"גרם", fat:37,  sugar:49, water:1,  protein:6 },
    { id:"loacker",          name:"🍪 לוואקר קוקוס",          unit:"גרם", fat:28,  sugar:42, water:2,  protein:5 },
    { id:"slashBrown",       name:"🍬 סלאש חום/בנט",          unit:"גרם", fat:8,   sugar:65, water:15, protein:2 },
    { id:"cornflakesCrunch", name:"🥣 קורנפלקס קראנץ'/כריות", unit:"גרם", fat:6,   sugar:38, water:3,  protein:6 },
    { id:"cornflakesAlufim", name:"🥣 קורנפלקס אלופים",       unit:"גרם", fat:1.5, sugar:30, water:3,  protein:7 },
    { id:"cocoaPowder",      name:"🍫 אבקת קקאו",             unit:"גרם", fat:12,  sugar:2,  water:3,  protein:20, note:"קקאו טהור ללא סוכר – מריר, הוסף ממתיק" },
    { id:"shokolait",        name:"🍫 שוקולית (אבקה)",        unit:"גרם", fat:2,   sugar:73, water:2,  protein:5,  note:"~2–3 כפות ל-500מ\"ל" },
  ],
  fruits: [
    { id:"strawberry",    name:"🍓 תות",              unit:"גרם", fat:0.3, sugar:7,   water:91, protein:0.7 },
    { id:"banana",        name:"🍌 בננה",             unit:"גרם", fat:0.3, sugar:23,  water:75, protein:1.1 },
    { id:"peach",         name:"🍑 אפרסק",            unit:"גרם", fat:0.3, sugar:10,  water:87, protein:0.9 },
    { id:"pineapple",     name:"🍍 אננס",             unit:"גרם", fat:0.1, sugar:13,  water:86, protein:0.5 },
    { id:"apple",         name:"🍎 תפוח",             unit:"גרם", fat:0.2, sugar:14,  water:86, protein:0.3 },
    { id:"cinnamonApple", name:"🍎 תפוח קינמון",      unit:"גרם", fat:0.5, sugar:15,  water:84, protein:0.4 },
    { id:"pear",          name:"🍐 אגס",              unit:"גרם", fat:0.1, sugar:15,  water:84, protein:0.4 },
    { id:"blueberry",     name:"🫐 אוכמניות",         unit:"גרם", fat:0.3, sugar:14,  water:84, protein:0.7 },
    { id:"raspberry",     name:"🫐 פטל",              unit:"גרם", fat:0.7, sugar:9,   water:86, protein:1.2 },
    { id:"cranberry",     name:"🍒 חמוציות",          unit:"גרם", fat:0.1, sugar:12,  water:87, protein:0.4 },
    { id:"watermelon",    name:"🍉 אבטיח",            unit:"גרם", fat:0.2, sugar:9,   water:91, protein:0.6 },
    { id:"mango",         name:"🥭 מנגו",             unit:"גרם", fat:0.4, sugar:17,  water:83, protein:0.8 },
    { id:"lemon",         name:"🍋 לימון",            unit:"גרם", fat:0.3, sugar:2.5, water:90, protein:1.1 },
    { id:"acaiFrozen",    name:"🫐 אסאי קפוא טהור",  unit:"גרם", fat:5,   sugar:2,   water:78, protein:2,   note:"מר מאוד – הוסף ממתיק" },
    { id:"acaiPack",      name:"🫐 אסאי פאק (ממותק)", unit:"גרם", fat:3.5, sugar:12,  water:75, protein:1.5 },
    { id:"acaiPowder",    name:"🫐 אבקת אסאי",        unit:"גרם", fat:6,   sugar:2,   water:5,  protein:2,   note:"מרוכז – ~5גר' לכפית" },
    { id:"sweetPotato",   name:"🍠 בטטה מבושלת",       unit:"גרם", fat:0.1, sugar:9,   water:80, protein:2,   note:"מבושלת ומעוכה – נותנת קרמיות טבעית" },
    { id:"avocado",       name:"🥑 אבוקדו",            unit:"גרם", fat:15,  sugar:0.7, water:73, protein:2,   note:"שומן בריא – מרקם קרמי מאוד, טעם עדין" },
  ],
};

const CATEGORY_LABELS = {
  dairy:"🥛 מוצרי חלב וחלב צמחי", coconut:"🥥 מוצרי קוקוס", nuts:"🥜 חמאות אגוזים",
  sweeteners:"🍯 ממתיקים", liquids:"💧 נוזלים", alcohol:"🍸 אלכוהול",
  mixins:"🍫 תוספות", fruits:"🍓 פירות",proteins:"💪חלבון",
};

// ─── לוגיקה ───────────────────────────────────────────────────────────────────

function getAllIngredients(customIngs=[]) {
  return [...Object.values(INGREDIENTS).flat(), ...customIngs];
}

function calcMacros(amounts, customIngs=[]) {
  const all=getAllIngredients(customIngs);
  let w=0,fat=0,sugar=0,water=0,protein=0
  for(const [id,amt] of Object.entries(amounts)){
    if(!amt||amt<=0) continue;
    const ing=all.find(i=>i.id===id); if(!ing) continue;
w+=amt; fat+=(ing.fat/100)*amt; sugar+=(ing.sugar/100)*amt;
const waterFactor = ing.isCommercialProteinBase ? 0.8 : 1;
water+=(ing.water/100)*amt*waterFactor;
protein+=((ing.protein||0)/100)*amt;
  }
  if(w===0) return null;
  return {weight:w, fat:fat/w*100, sugar:sugar/w*100, water:water/w*100, protein:protein/w*100};
}

// ─── זיהוי סוג לפי מרכיב דומיננטי ──────────────────────────────────────────

const DAIRY_IDS_DET   = ["milk3","cream38","cookingCream","sourCream","greekYogurt","greekYogurt10","regularYogurt","condensedMilk"];
const PLANT_IDS_DET   = ["almondMilk","soyMilk","hazelnutMilk","oatMilk"];
const COCONUT_IDS_DET = ["coconutMilk","coconutCream","coconutOil"];

function detectType(amounts={}) {
  const totalW = Object.values(amounts).reduce((s,v)=>s+(v||0),0);
  if(totalW===0) return {type:"dairy", confidence:0, label:"\u05d7\u05dc\u05d1\u05d9"};

  const sum = ids => ids.reduce((s,id)=>s+(amounts[id]||0),0);
  const pct = w => w/totalW;

  const alcW     = sum(ALCOHOL_IDS);
  const fruitW   = sum(FRUIT_IDS);
  const coconutW = sum(COCONUT_IDS_DET);
  const plantW   = sum(PLANT_IDS_DET);
  const dairyW   = sum(DAIRY_IDS_DET);

  // חשב מאקרו אמיתי של התערובת
  const macros = calcMacros(amounts);
  const fatPct   = macros ? macros.fat   : 0;
  const sugarPct = macros ? macros.sugar : 0;
  const waterPct = macros ? macros.water : 0;
  const proteinPct = macros ? macros.protein : 0;

  // חלבון – חלבון גבוה + שומן נמוך-בינוני
if(proteinPct >= 6 && fatPct < 10)
  return {type:"protein", confidence:proteinPct/15, label:"\u05d7\u05dc\u05d1\u05d5\u05df"};

  // אלכוהול – גובר על הכל אם יש יותר מ-8%
  if(pct(alcW)>0.08) return {type:"cocktail", confidence:pct(alcW), label:"\u05e7\u05d5\u05e7\u05d8\u05d9\u05d9\u05dc"};

  // סורבה – שומן נמוך מאוד + מים גבוהים + פרי דומיננטי
  if(fatPct < 3 && waterPct > 60 && pct(fruitW) > 0.35)
    return {type:"sorbet", confidence:pct(fruitW), label:"\u05e1\u05d5\u05e8\u05d1\u05d4"};

  // פרי+קוקוס
  if(pct(fruitW)>0.2 && pct(coconutW)>0.15 && pct(fruitW+coconutW)>0.6)
    return {type:"fruitCoconut", confidence:pct(fruitW+coconutW), label:"\u05e4\u05e8\u05d9 & \u05e7\u05d5\u05e7\u05d5\u05e1"};

  // קוקוס – קוקוס דומיננטי
  if(pct(coconutW) > 0.4)
    return {type:"coconut", confidence:pct(coconutW), label:"\u05e7\u05d5\u05e7\u05d5\u05e1"};

  // טבעוני – צמחי דומיננטי
  if(pct(plantW) > 0.4 && pct(dairyW) < 0.2)
    return {type:"vegan", confidence:pct(plantW), label:"\u05d8\u05d1\u05e2\u05d5\u05e0\u05d9"};

  // חלבי/שמנת – לפי אחוזי שומן בפועל
  if(pct(dairyW) > 0.3 || pct(plantW) > 0.2) {
    if(fatPct >= 13)
      return {type:"heavyCream", confidence:fatPct/20, label:"\u05e9\u05de\u05e0\u05ea \u05db\u05d1\u05d3\u05d4"};
    return {type:"dairy", confidence:pct(dairyW), label:"\u05d7\u05dc\u05d1\u05d9"};
  }

  // מעורב
  return {type:"mixed", confidence:0.3, label:"\u05de\u05e2\u05d5\u05e8\u05d1"};
}

// טווח מעורב – ממוצע של כל הסוגים
const MIXED_TARGET = {
  fat:     [7,  13],
  sugar:   [14, 21],
  water:   [58, 72],
  protein: [4,  10],
};

function getTarget(typeOrAuto, amounts={}) {
  const totalW = Object.values(amounts).reduce((s,v)=>s+(v||0),0);

  if(typeOrAuto === "mixed") {
    if(totalW === 0) return {...MIXED_TARGET};
    const sum = ids => ids.reduce((s,id)=>s+(amounts[id]||0),0);
    const dairyW   = sum(DAIRY_IDS_DET);
    const plantW   = sum(PLANT_IDS_DET);
    const coconutW = sum(COCONUT_IDS_DET);
    const fruitW   = sum(FRUIT_IDS);
    const alcW     = sum(ALCOHOL_IDS);
    const weights  = {
      dairy:        (dairyW + plantW*0.5) / totalW,
      coconut:      coconutW / totalW,
      sorbet:       fruitW   / totalW,
      cocktail:     alcW     / totalW,
      vegan:        plantW*0.5 / totalW,
      fruitCoconut: 0, // לא משוקלל ישירות – מחושב ע"י sorbet+coconut
    };
const macros = ["fat","sugar","water","protein"];
    const result = {};
    for(const m of macros){
      let lo=0, hi=0, totalPct=0;
      for(const [t,w] of Object.entries(weights)){
        if(w>0 && TARGETS[t] && TARGETS[t][m]){ lo+=TARGETS[t][m][0]*w; hi+=TARGETS[t][m][1]*w; totalPct+=w; }
      }
      const rem=1-totalPct;
      if(rem>0){ lo+=MIXED_TARGET[m][0]*rem; hi+=MIXED_TARGET[m][1]*rem; }
      result[m]=[Math.round(lo*10)/10, Math.round(hi*10)/10];
    }
    if(fruitW/totalW>0.2) result.water=[result.water[0], Math.min(result.water[1]+4, 82)];
    return result;
  }

// cocktail = modifier על גבי הבסיס האמיתי, לא פרופיל עצמאי
  const effectiveType = typeOrAuto === "cocktail" ? "dairy" : typeOrAuto;
  const base = TARGETS[effectiveType] ? {...TARGETS[effectiveType]} : {...MIXED_TARGET};

  if (totalW > 0) {
    const fruitW = FRUIT_IDS.reduce((s,id) => s + (amounts[id]||0), 0);
    if (fruitW / totalW > 0.2)
      base.water = [base.water[0], Math.min(base.water[1] + 5, 82)];

    if (effectiveType === "sorbet" && fruitW > 0) {
      const SOUR_FRUITS = ["strawberry","raspberry","cranberry","lemon","orangeJuice","lemonJuice","acaiFrozen","acaiPack","acaiPowder"];
      const SWEET_FRUITS = ["mango","banana","watermelon","pineapple","apple","pear","peach","cinnamonApple"];
      const sourW = SOUR_FRUITS.reduce((s,id) => s + (amounts[id]||0), 0);
      const sweetW = SWEET_FRUITS.reduce((s,id) => s + (amounts[id]||0), 0);
      if (sourW > sweetW && sourW / fruitW > 0.5)
        base.sugar = [28, 32];
      else if (sweetW > sourW && sweetW / fruitW > 0.5)
        base.sugar = [24, 28];
    }

    // התאמת מייצבים
    const xanthan = amounts["xanthan"] || 0;
    const guar    = amounts["guarGum"] || 0;
    const xPct    = xanthan / totalW * 100;
    const gPct    = guar    / totalW * 100;
    const hasX    = xPct >= 0.05;
    const hasG    = gPct >= 0.1;

    if (hasX || hasG) {
      const t = effectiveType;
      let sugarAdj = 0, fatAdj = 0;
      if (hasX && hasG) {
        if (t==="dairy"||t==="heavyCream") { sugarAdj=2; fatAdj=1.5; }
        else if (t==="sorbet")             { sugarAdj=6; }
        else if (t==="protein")            { sugarAdj=3; fatAdj=1; }
        else if (t==="coconut")            { sugarAdj=2.5; fatAdj=1.5; }
        else                               { sugarAdj=2; }
      } else if (hasG) {
        if (t==="dairy"||t==="heavyCream") { sugarAdj=1.5; fatAdj=1; }
        else if (t==="sorbet")             { sugarAdj=4; }
        else if (t==="protein")            { sugarAdj=2; }
        else if (t==="coconut")            { sugarAdj=2; fatAdj=1; }
        else                               { sugarAdj=1.5; }
      } else if (hasX) {
        if (t==="dairy"||t==="heavyCream") { sugarAdj=1.5; fatAdj=1.5; }
        else if (t==="sorbet")             { sugarAdj=3; }
        else if (t==="protein")            { sugarAdj=2; }
        else if (t==="coconut")            { sugarAdj=1.5; fatAdj=1.5; }
        else                               { sugarAdj=1.5; }
      }
      if (sugarAdj > 0) base.sugar = [Math.max(0, base.sugar[0]-sugarAdj), Math.max(0, base.sugar[1]-sugarAdj)];
      if (fatAdj > 0)   base.fat   = [Math.max(0, base.fat[0]-fatAdj),     Math.max(0, base.fat[1]-fatAdj)];
    }

    // אלכוהול כ-modifier אחרון
    const ethanolPct = calcEthanolPct(amounts);
    const withAlcohol = applyAlcoholModifier(base, ethanolPct);
    const puddingAmt = amounts["instantPudding"] || 0;
    return applyPuddingModifier(withAlcohol, puddingAmt, effectiveType);
  }

  return base;
}

// ── אלכוהול כ-Modifier דינמי ─────────────────────────────────────────────────
function calcEthanolPct(amounts, customIngs=[]) {
  const all = getAllIngredients(customIngs);
  let ethanolMl = 0, totalW = 0;
  for (const [id, amt] of Object.entries(amounts)) {
    if (!amt || amt <= 0) continue;
    totalW += amt;
    const ing = all.find(i => i.id === id);
    if (ing?.abv) ethanolMl += amt * (ing.abv / 100);
  }
  return totalW > 0 ? ethanolMl / totalW * 100 : 0;
}

function applyAlcoholModifier(baseTarget, ethanolPct) {
  // אם אין אלכוהול משמעותי – מחזירים את הטווח כמו שהוא
  if (ethanolPct < 0.5) return baseTarget;
  return {
    ...baseTarget,
    water: [baseTarget.water[0], Math.max(baseTarget.water[0] + 2, baseTarget.water[1] - 3)],
    sugar: [baseTarget.sugar[0], Math.max(baseTarget.sugar[0] + 2, baseTarget.sugar[1] - 2)],
  };
}
function applyPuddingModifier(baseTarget, puddingAmt, detectedType) {
  if (!puddingAmt || puddingAmt < 5) return baseTarget;
  const modified = {
    ...baseTarget,
    water: [baseTarget.water[0], baseTarget.water[1] + 2],
    fat:   [Math.max(0, baseTarget.fat[0] - 2),   baseTarget.fat[1]],
    sugar: [Math.max(0, baseTarget.sugar[0] - 2),  baseTarget.sugar[1]],
  };
  if (detectedType === "protein") {
    modified.sugar[0] = 6;
    modified.fat[0]   = 2;
  }
  return modified;
}

function calcScore(macros, detectedType, amounts={}) {
  if(!macros) return 0;
  const t=getTarget(detectedType, amounts);
  // בתוך הטווח = 0 עונש. מחוץ = עונש גדל מהר
  const pen=(v,lo,hi,max,k)=>{ if(v>=lo&&v<=hi) return 0; return Math.min(max,(v<lo?lo-v:v-hi)*k); };
  let s=100;
  s-=pen(macros.fat,   t.fat[0],   t.fat[1],   40, 4.5);  // שומן – עונש חזק
  s-=pen(macros.sugar, t.sugar[0], t.sugar[1], 35, 3.5);
  s-=pen(macros.water, t.water[0], t.water[1], 30, 2);
  // חריגה בשומן מעל 3% → מקסימום 75 (לא "מושלם")
  if(macros.fat > t.fat[1]+3 || macros.fat < t.fat[0]-3) s=Math.min(s,74);
  if(macros.sugar> t.sugar[1]+3|| macros.sugar<t.sugar[0]-3) s=Math.min(s,74);
  return Math.max(0,Math.round(s));
}

function calcTotalOf(macro, excludeIds, amounts, customIngs=[]) {
  const all=getAllIngredients(customIngs);
  return Object.entries(amounts).reduce((s,[id,amt])=>{ if(!amt||excludeIds.includes(id)) return s; const ing=all.find(i=>i.id===id); return ing?s+(ing[macro]/100)*amt:s; },0);
}
function baseW(excludeIds, amounts) { return Object.entries(amounts).reduce((s,[id,amt])=>(!amt||excludeIds.includes(id)?s:s+amt),0); }

function generateSuggestions(macros, amounts, detectedType, customIngs=[]) {
  const t=getTarget(detectedType,amounts); const suggs=[];
  const generateSuggestions = (macros, t, detectedType, amounts) => {
    const suggs = [];
    
    // בדיקה אגרסיבית לזיהוי קוקוס/פרווה:
    // 1. האם השם של היעד מכיל קוקוס או פרי?
    // 2. האם המשתמש בחר רכיב עם המילה קוקוס?
    // 3. האם זה מזוהה כקוקוס?
    const isCoconutPath = 
      (t && t.name && (t.name.includes('קוקוס') || t.name.includes('Coconut'))) || 
      (detectedType && (detectedType.toLowerCase().includes('coconut') || detectedType.includes('קוקוס'))) ||
      (amounts && Object.keys(amounts).some(id => id.toLowerCase().includes('coconut') || id.includes('קוקוס')));

    // --- בדיקת שומן ---
    if (macros.fat < t.fat[0]) {
      const missingFatGrams = (t.fat[0] - macros.fat) * 5; 

      if (isCoconutPath) {
        // המלצות פרווה
        const creamAmount = Math.round(missingFatGrams / 0.20); 
        const oilAmount = Math.round(missingFatGrams);
        
        suggs.push({
          sev: "warn", 
          text: `🥥 חסר שומן: הוסף ${creamAmount} גרם קרם קוקוס (20%) או ${oilAmount} גרם שמן קוקוס`
        });
      } else {
        // המלצה חלבית
        const n = Math.max(0, Math.round(((t.fat[0] / 100) * 500))); 
        suggs.push({
          sev: "warn", 
          text: `🥛 חסר שומן: יש להוסיף כ-${n} גרם שמנת 38% למרקם אידיאלי`
        });
      }
    } else if (macros.fat > t.fat[1]) {
      suggs.push({sev: "danger", text: `⚠️ יותר מדי שומן (${macros.fat.toFixed(1)}%)`});
    }

    // --- בדיקת סוכר ---
    if (macros.sugar < t.sugar[0]) {
      suggs.push({sev: "warn", text: `🍭 חסר סוכר (${macros.sugar.toFixed(1)}%)` });
    } else if (macros.sugar > t.sugar[1]) {
      suggs.push({sev: "danger", text: `⚠️ יותר מדי סוכר (${macros.sugar.toFixed(1)}%)` });
    }

    // --- בדיקת מוצקים ---
    if (macros.msnf < t.msnf[0]) {
      const msg = isCoconutPath 
        ? "🥥 חסר מוצקים: שקול להוסיף מעט אבקת חלב קוקוס או סיבים תזונתיים" 
        : "🥛 חסר מוצקי חלב: מומלץ להוסיף אבקת חלב רזה";
      suggs.push({sev: "warn", text: msg});
    }

    return suggs;
  };
  if(macros.sugar<t.sugar[0]){
    const n=Math.max(0,Math.round((t.sugar[0]/100)*baseW(SWEETENER_IDS,amounts)-calcTotalOf("sugar",SWEETENER_IDS,amounts,customIngs)));
    suggs.push({sev:"warn", text:`מעט מדי סוכר – הוסף כ-${n}גר' סוכר (או ${Math.round(n*1.22)}גר' דבש)`, fixType:"sugar", fixAmt:n});
  } else if(macros.sugar>t.sugar[1]){
    suggs.push({sev:"danger", text:`יותר מדי סוכר (${macros.sugar.toFixed(1)}%) – הפחת ממתיקים`});
  }
  if(macros.water<t.water[0]){
    const n=Math.max(0,Math.round((t.water[0]/100)*baseW(LIQUID_IDS,amounts)-calcTotalOf("water",LIQUID_IDS,amounts,customIngs)));
    suggs.push({sev:"warn", text:`מעט מדי מים – הוסף כ-${n}מ"ל`, fixType:"water", fixAmt:n});
  } else if(macros.water>t.water[1]){
    suggs.push({sev:"danger", text:`יותר מדי מים (${macros.water.toFixed(1)}%) – הפחת נוזלים`});
  }
  if((amounts["allulose"]||0)>0) suggs.push({sev:"info", text:"🔬 אלולוז: 0% סוכר אפקטיבי – אם רך מדי, הפחת מעט"});

  // חישוב אתנול אמיתי לפי ABV של כל משקה
  const ALL_INGS_FOR_ALC = getAllIngredients(customIngs);
  let ethanolMl = 0;
  for(const id of ALCOHOL_IDS){
    const amt = amounts[id]||0; if(!amt) continue;
    const ing = ALL_INGS_FOR_ALC.find(i=>i.id===id);
    const abv = ing?.abv||0;
    ethanolMl += amt * (abv/100);
  }
  // גם מרכיבים מותאמים עם abv
  for(const ing of customIngs){
    if(ing.abv&&(amounts[ing.id]||0)>0) ethanolMl += amounts[ing.id]*(ing.abv/100);
  }
  const ethanolPct = macros.weight>0 ? ethanolMl/macros.weight*100 : 0;

  if(ethanolPct>2){
    suggs.push({sev:"danger", text:`⚠️ אתנול ${ethanolPct.toFixed(2)}% – גבוה מדי! הגלידה לא תתקשה. יעד: עד 1.5–2%`});
  } else if(ethanolPct>1.5){
    suggs.push({sev:"warn", text:`🍸 אתנול ${ethanolPct.toFixed(2)}% – בגבול העליון. מרקם עשוי להיות רך, הקפא 48 שעות`});
  } else if(ethanolPct>0){
    suggs.push({sev:"info", text:`🍸 אתנול ${ethanolPct.toFixed(2)}% – טווח אופטימלי ✅`});
  }
  // ── אזורי סיכון ──────────────────────────────────────────────────────────────
  if (macros.fat > RISK_ZONES.fat.danger)
    suggs.push({sev:"danger", text: RISK_ZONES.fat.msg});

  if (macros.protein > RISK_ZONES.protein.danger)
    suggs.push({sev:"danger", text: RISK_ZONES.protein.msg});

  if (macros.water > RISK_ZONES.water.danger)
    suggs.push({sev:"danger", text: RISK_ZONES.water.msg});

  const MILKY_TYPES = ["dairy","heavyCream","protein","mixed","vegan","cocktail"];
  if (macros.sugar < RISK_ZONES.sugarLow.threshold && MILKY_TYPES.includes(detectedType))
    suggs.push({sev:"danger", text: RISK_ZONES.sugarLow.msg});

  // אזהרת אלכוהול קשיחה
  const ethanolPctCheck = calcEthanolPct(amounts, customIngs);
  if (ethanolPctCheck > 2)
    suggs.push({sev:"danger", text:`🍸 כמות האלכוהול גבוהה מדי (${ethanolPctCheck.toFixed(2)}% מהמשקל הכולל). אלכוהול מונע קיפאון – הגלידה לא תצליח להתמצק. מומלץ להפחית.`});
  else if (ethanolPctCheck >= 1 && ethanolPctCheck <= 2)
    suggs.push({sev:"info", text:`🍸 אתנול ${ethanolPctCheck.toFixed(2)}% – טווח אופטימלי ✅`});

  // כלל אצבע: שומן גבוה + סוכר נמוך
  if (macros.fat > 13 && macros.sugar < 15)
    suggs.push({sev:"warn", text:"🧈 שומן גבוה עם סוכר נמוך – כדאי לאזן עם ממתיק נוסף"});

  // כלל אצבע: חלבון גבוה + סוכר נמוך
  if (macros.protein > 12 && macros.sugar < 12)
    suggs.push({sev:"warn", text:"💪 חלבון גבוה עם מעט סוכר – הוסף ממתיק או מייצב למרקם חלק"});

  // כלל אצבע: מים גבוהים
  if (macros.water > 73)
    suggs.push({sev:"warn", text:"💧 תכולת מים גבוהה – שקל להוסיף אבקת חלב, חלב מרוכז, או סוכר"});
  // ── אזהרות אינסטנט פודינג ──────────────────────────────────────────
  const puddingAmt = amounts["instantPudding"] || 0;
  if (puddingAmt > 0) {
    const hasCoconut = (amounts["coconutMilk"]||0) + (amounts["coconutCream"]||0) > 0;
    if (hasCoconut)
      suggs.push({sev:"warn", text:"🥥🍮 שילוב קוקוס + פודינג עלול לייצר מרקם צמיגי/כבד. מומלץ להפחית באחד מהם."});
    if (detectedType === "sorbet" && puddingAmt > 10)
      suggs.push({sev:"warn", text:"🍧🍮 עודף פודינג בסורבה – יהפוך אותו לג'ל מלאכותי. עד 10גר' בלבד."});
    if (detectedType === "vegan")
      suggs.push({sev:"danger", text:"🌱⚠️ אינסטנט פודינג רגיל מכיל מוצקי חלב – הופך את המתכון ללא טבעוני!"});
    if (puddingAmt >= 5 && puddingAmt <= 15 && detectedType !== "sorbet" && detectedType !== "vegan")
      suggs.push({sev:"info", text:`🍮 אינסטנט פודינג ${puddingAmt}גר' – טווח אופטימלי ✅`});
    if (puddingAmt > 15)
      suggs.push({sev:"warn", text:`🍮 פודינג ${puddingAmt}גר' – כמות גבוהה מדי. יעד: 5–15גר'.`});
  }
  // אזהרת שמן קוקוס טהור
  const coconutOilAmt = amounts["coconutOil"] || 0;
  const coconutOilPct = macros.weight > 0 ? coconutOilAmt / macros.weight * 100 : 0;
  if (coconutOilPct > COCONUT_OIL_MAX_PCT)
    suggs.push({sev:"danger", text:`🥥 שמן קוקוס טהור ${coconutOilPct.toFixed(1)}% מהמתכון – יוצר מרקם שעוותי/גרגירי מגעיל! השמן קופא בחלקיקי שעווה נפרדים. החלף בקרם קוקוס מפחית (17–22% שומן) שהשומן בו כבר באמולסיה טבעית.`});
  else if (coconutOilAmt > 0)
    suggs.push({sev:"warn", text:`🥥 שמן קוקוס טהור – כמות קטנה (${coconutOilPct.toFixed(1)}%). עדיף להחליף בקרם קוקוס למרקם חלק יותר.`});
  if(suggs.length===0) suggs.push({sev:"success", text:"✨ האיזון מושלם! הגלידה אמורה לצאת חלקה וטעימה"});
  return suggs;
}

function autoBalance(amounts, type, customIngs=[], targetW=500) {
  // "mixed" → השתמש ב-dairy כברירת מחדל לאיזון
  const effectiveType = (!type || type==="mixed" || !TARGETS[type]) ? "dairy" : type;
  const t=TARGETS[effectiveType];
  const correctors=[...SWEETENER_IDS,...FAT_IDS,...LIQUID_IDS];
  const bw=Object.entries(amounts).reduce((s,[id,amt])=>(!amt||correctors.includes(id)?s:s+amt),0);
  if(bw===0) return amounts;
  const mid={fat:(t.fat[0]+t.fat[1])/2, sugar:(t.sugar[0]+t.sugar[1])/2, water:(t.water[0]+t.water[1])/2};
  const curFat=calcTotalOf("fat",correctors,amounts,customIngs);
  const curSugar=calcTotalOf("sugar",correctors,amounts,customIngs);
  const curWater=calcTotalOf("water",correctors,amounts,customIngs);
  const cAdd=Math.max(0,Math.round(((mid.fat/100)*bw-curFat)/0.38));
  const w1=bw+cAdd;
  const sAdd=Math.max(0,Math.round((mid.sugar/100)*w1-curSugar));
  const w2=w1+sAdd;
  const wAdd=Math.max(0,Math.round((mid.water/100)*w2-curWater-cAdd*0.56));
  return {...amounts, ...(cAdd>0?{cream38:(amounts.cream38||0)+cAdd}:{}), ...(sAdd>0?{sugar:(amounts.sugar||0)+sAdd}:{}), ...(wAdd>0?{water:(amounts.water||0)+wAdd}:{})};
}

// ─── buildRecipe ─────────────────────────────────────────────────────────────

const MED_BASES=["coconutMilk","coconutCream","cookingCream","sourCream"];

function buildRecipe(chosenIds, type, customIngs=[], targetWeight=500, primaryId=null) {
  const effectiveBuildType = (!type || type==="mixed" || !TARGETS[type]) ? "dairy" : type;
  const t=TARGETS[effectiveBuildType];
 const tFat   = Math.min((t.fat[0]+t.fat[1])/2,   t.fat[1]   - 1);
  const tSugar = Math.min((t.sugar[0]+t.sugar[1])/2, t.sugar[1] - 1);
  const tWater = Math.min((t.water[0]+t.water[1])/2, t.water[1] - 1);

  // חוק פרי: פרי > 20% מהמרכיבים → הפחת יעד שומן
  const fruitChosenCount = chosenIds.filter(id => FRUIT_IDS.includes(id)).length;
  const hasFruitHeavy = fruitChosenCount > 0 && fruitChosenCount / chosenIds.length > 0.2;
  const effectiveTFat = hasFruitHeavy ? Math.max(tFat - 2.5, t.fat[0]) : tFat;
  const TW=targetWeight*0.88;
  const all=getAllIngredients(customIngs);
  const ingOf=id=>all.find(x=>x.id===id);

  const isSweet=id=>SWEETENER_IDS.includes(id);
  const isAlc=id=>ALCOHOL_IDS.includes(id);
  const isFruit=id=>FRUIT_IDS.includes(id);
  const isMixin=id=>{const i=ingOf(id);return i&&i.sugar>30&&i.fat>15&&!isSweet(id)&&!isAlc(id);};
  const isDryFat=id=>{const i=ingOf(id);return i&&i.fat>=25&&i.water<40&&!isSweet(id)&&!isMixin(id)&&!isAlc(id);};
  // אם יש primaryId והוא פרי – פירות יכולים להיות בסיס
  // אם אין primaryId – כל הפירות המימיים הם בסיסים שווים
  const isPureBase=id=>{
    const i=ingOf(id);
    if(!i||isSweet(id)||isAlc(id)) return false;
    if(isFruit(id)) return i.water>=60; // פירות מימיים = בסיס תמיד
    return i.water>=65&&i.fat<10;
  };
  const isMedBase=id=>{
    if(isFruit(id)) return false;
    return MED_BASES.includes(id)||(()=>{const i=ingOf(id);return i&&i.water>=50&&i.fat>=10&&i.fat<25&&!isSweet(id)&&!isAlc(id)&&!isMixin(id);})();
  };

  // מרכיבי FLAVOR – חמאות אגוזים/פיסטוק הן הטעם הראשי, לא רק מתקני שומן
  const NUT_FLAVOR_IDS = ["almondButter","hazelnutButter","peanutButter","nutButter","pistachioPure","pistachioSweet"];
  const isNutFlavor = id => NUT_FLAVOR_IDS.includes(id);

  const mixins=chosenIds.filter(isMixin), alcs=chosenIds.filter(isAlc);
  // חמאות אגוזים שנבחרו = flavor, לא fatCorr
  const nutFlavors=chosenIds.filter(isNutFlavor);
  const fatCorrs=chosenIds.filter(id=>isDryFat(id)&&!isMixin(id)&&!isNutFlavor(id));
  const sweets=chosenIds.filter(isSweet);
  const pureBases=chosenIds.filter(id=>isPureBase(id)&&!isMixin(id)&&!isDryFat(id));
  const medBases=chosenIds.filter(id=>isMedBase(id)&&!isMixin(id)&&!isDryFat(id));

  const allBases=[...pureBases,...medBases];
  const extras=chosenIds.filter(id=>![...mixins,...alcs,...fatCorrs,...sweets,...allBases,...nutFlavors].includes(id));

  const fixed={};
  mixins.forEach(id=>{fixed[id]=40;}); alcs.forEach(id=>{fixed[id]=50;});
  fatCorrs.slice(1).forEach(id=>{fixed[id]=25;}); sweets.slice(1).forEach(id=>{fixed[id]=15;});
  extras.forEach(id=>{fixed[id]=40;});

  // חמאות אגוזים – לפחות 20% מהנפח, מינימום 100גר' כדי שהטעם יורגש
  const nutFlavorAmt = Math.max(100, Math.round(TW * 0.22 / 5) * 5);
  if(nutFlavors.length===1){
    fixed[nutFlavors[0]] = nutFlavorAmt;
  } else if(nutFlavors.length>1){
    // אם נבחרו כמה – הראשי מקבל יותר, שאר מחולקים
    const primary = primaryId && nutFlavors.includes(primaryId) ? primaryId : nutFlavors[0];
    fixed[primary] = nutFlavorAmt;
    nutFlavors.filter(id=>id!==primary).forEach(id=>{fixed[id]=Math.round(nutFlavorAmt*0.5/5)*5;});
  }

  // בסיסים משניים (לא הראשי) → חלק יחסי מהנפח הכולל
  // הרעיון: כל הבסיסים חולקים ביניהם את TW*0.75, הראשי מקבל 60%, שאר 40% מחולק שווה
  const allByWater=chosenIds.map(id=>({id,w:ingOf(id)?.water||0})).sort((a,b)=>b.w-a.w);
  const sBase = primaryId && chosenIds.includes(primaryId)
    ? primaryId
    : (pureBases[0]||medBases[0]||(allByWater[0]?.w>30?allByWater[0].id:null));

  // חלוקת נפח בין הבסיסים:
  // יש primaryId → ראשי ~60% מהנפח, שאר ~30% מחולק שווה
  // אין primaryId → כל הבסיסים שווים, הסולבר מחשב את הראשי ושאר מקבלים ~40%/n
  const secondaryBases = allBases.filter(id=>id!==sBase);
  if(secondaryBases.length>0){
    const secPct = primaryId ? 0.30 : 0.40;
    const secAmt = Math.round(TW * secPct / secondaryBases.length / 5) * 5;
    secondaryBases.forEach(id=>{fixed[id]=Math.max(50, secAmt);});
  }
  const sFat=fatCorrs.filter(id=>id!==sBase)[0]||null;
  const sSweet=sweets[0]||null;
  const ib=sBase?ingOf(sBase):null, ip=sFat?ingOf(sFat):null, is_=sSweet?ingOf(sSweet):null;

  const fW=Object.values(fixed).reduce((s,v)=>s+v,0);
  const fFG=Object.entries(fixed).reduce((s,[id,a])=>{const i=ingOf(id);return i?s+(i.fat/100)*a:s;},0);
  const fSG=Object.entries(fixed).reduce((s,[id,a])=>{const i=ingOf(id);return i?s+(i.sugar/100)*a:s;},0);
  const fWG=Object.entries(fixed).reduce((s,[id,a])=>{const i=ingOf(id);return i?s+(i.water/100)*a:s;},0);
  const R=TW-fW, rFat=effectiveTFat/100*TW-fFG, rSug=tSugar/100*TW-fSG, rWat=tWater/100*TW-fWG;
  const cl=(v,mn,mx)=>Math.max(mn,Math.min(mx,Math.round(v/5)*5));

  if(ib&&ib.fat>10&&!pureBases[0]&&!medBases[0]){
    const B=rFat/(ib.fat/100), W=t.water[0]/100*TW-fWG-ib.water/100*B;
    if(sBase)fixed[sBase]=cl(B,20,250); if(W>0)fixed["water"]=(fixed["water"]||0)+cl(W,0,300);
    if(sFat)fixed[sFat]=25;
    if(sSweet){const si=ingOf(sSweet)||{sugar:100};const S=(rSug-(ib.sugar/100)*B)/(si.sugar/100);fixed[sSweet]=cl(S,10,150);}
    let m0=calcMacros(fixed,customIngs);
    if(m0&&m0.sugar<t.sugar[0]){const sid=sSweet||"sugar";const si=ingOf(sid)||{sugar:100};const d=si.sugar/100-tSugar/100;if(d>0.001){const x=(tSugar/100*m0.weight-m0.sugar/100*m0.weight)/d;fixed[sid]=(fixed[sid]||0)+Math.max(10,Math.min(120,cl(x,0,120)));}}
    Object.keys(fixed).forEach(k=>{if(!fixed[k]||fixed[k]<=0)delete fixed[k];}); return fixed;
  }

  let B=0,F=0,S=0;
  if(ib&&ip&&is_){
    const bF=ib.fat/100,fF=ip.fat/100,sF=is_.fat/100,bS=ib.sugar/100,fS=ip.sugar/100,sS=is_.sugar/100;
    const det=bF*(fS-sS)-fF*(bS-sS)+sF*(bS-fS);
    if(Math.abs(det)>0.0001){B=(rFat*(fS-sS)-fF*(rSug-sS*R)+sF*(rSug-fS*R))/det;F=(bF*(rSug-sS*R)-rFat*(bS-sS)+sF*(bS*R-rSug))/det;S=(bF*(fS*R-rSug)-fF*(bS*R-rSug)+rFat*(bS-fS))/det;}
    else{B=R*0.6;F=R*0.2;S=R*0.2;}
  } else if(ib&&ip){const det=ip.fat/100-ib.fat/100;if(Math.abs(det)>0.001){F=(rFat-ib.fat/100*R)/det;B=R-F;}else{B=R*0.6;F=R*0.4;}}
  else if(ib&&is_){const det=is_.sugar/100-ib.sugar/100;if(Math.abs(det)>0.001){S=(rSug-ib.sugar/100*R)/det;B=R-S;}else{B=R*0.8;S=R*0.2;}}
  else if(ib){B=R;}else if(ip){F=R;}else if(is_){S=R;}

  if(sBase)fixed[sBase]=cl(B,30,600); if(sFat)fixed[sFat]=cl(F,20,200); if(sSweet)fixed[sSweet]=cl(S,10,150);

  let m=calcMacros(fixed,customIngs);
  if(m&&m.fat>t.fat[1]&&sFat&&fixed[sFat]>20){const i2=ingOf(sFat);const ex=(m.fat-effectiveTFat)/100*m.weight;fixed[sFat]=Math.max(20,fixed[sFat]-Math.min(fixed[sFat]-20,Math.round(ex/(i2.fat/100)/5)*5));}
  m=calcMacros(fixed,customIngs);
  if(m){if(m.sugar>t.sugar[1]&&sSweet&&fixed[sSweet]>10){const i2=ingOf(sSweet);const ex=(m.sugar-tSugar)/100*m.weight;fixed[sSweet]=Math.max(10,fixed[sSweet]-Math.min(fixed[sSweet]-10,Math.round(ex/(i2.sugar/100)/5)*5));}
  else if(m.sugar<t.sugar[0]){const sid=sSweet||"sugar";const si=ingOf(sid)||{sugar:100};const d=si.sugar/100-tSugar/100;if(d>0.001){const x=(tSugar/100*m.weight-m.sugar/100*m.weight)/d;fixed[sid]=(fixed[sid]||0)+Math.max(10,Math.min(150,Math.round(x/5)*5));}}}
  m=calcMacros(fixed,customIngs);
  if(m&&m.water>t.water[1]){const cands=[...pureBases,...medBases,...extras].filter(id=>fixed[id]&&ingOf(id)&&(ingOf(id).water||0)>60);cands.sort((a,b)=>(ingOf(b).water/100*(fixed[b]||0))-(ingOf(a).water/100*(fixed[a]||0)));for(const id of cands){const i2=ingOf(id);const ex=(m.water-tWater)/100*m.weight;const cut=Math.min(fixed[id]-30,Math.round(ex/(i2.water/100)/5)*5);if(cut>0){fixed[id]=Math.max(30,fixed[id]-cut);break;}}}
  m=calcMacros(fixed,customIngs);
  if(m&&m.water<t.water[0]){const x=(tWater/100*m.weight-m.water/100*m.weight)/(1-tWater/100);fixed["water"]=(fixed["water"]||0)+Math.max(0,Math.round(x/5)*5);}
  m=calcMacros(fixed,customIngs);
  if(m&&m.fat<t.fat[0]&&!sFat&&t.fat[0]>2){const af=["coconut","vegan"].includes(type)?"coconutOil":"cream38";const ai=ingOf(af);if(ai){const d=ai.fat/100-effectiveTFat/100;if(d>0.01){const x=(effectiveTFat/100*m.weight-m.fat/100*m.weight)/d;fixed[af]=(fixed[af]||0)+Math.max(20,Math.min(150,Math.round(x/5)*5));}}}
  m=calcMacros(fixed,customIngs);
  if(m&&m.sugar<t.sugar[0]){const sid=sSweet||"sugar";const si=ingOf(sid)||{sugar:100};const d=si.sugar/100-tSugar/100;if(d>0.001){const x=(tSugar/100*m.weight-m.sugar/100*m.weight)/d;if(x>0)fixed[sid]=(fixed[sid]||0)+Math.max(5,Math.min(120,Math.round(x/5)*5));}}
  m=calcMacros(fixed,customIngs);
  if(m&&m.weight<targetWeight*0.75){const pad=sBase||"water";fixed[pad]=(fixed[pad]||0)+Math.ceil((targetWeight*0.75-m.weight)/5)*5;}
  Object.keys(fixed).forEach(k=>{if(!fixed[k]||fixed[k]<=0)delete fixed[k];}); return fixed;
}

// ─── קומפוננטות ───────────────────────────────────────────────────────────────

function ScoreRing({score}){
  const color=score>=80?"#2D8A4E":score>=55?"#F59E0B":"#E8394D";
  const emoji=score>=80?"🏆":score>=55?"👍":"⚠️";
  const label=score>=80?"מושלם":score>=55?"טוב":"צריך שיפור";
  const r=28,circ=2*Math.PI*r,fill=(score/100)*circ;
  return(<div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,minWidth:70}}>
    <div style={{position:"relative",width:70,height:70}}>
      <svg width={70} height={70} style={{transform:"rotate(-90deg)"}}>
        <circle cx={35} cy={35} r={r} fill="none" stroke="#eee" strokeWidth={6}/>
        <circle cx={35} cy={35} r={r} fill="none" stroke={color} strokeWidth={6} strokeDasharray={`${fill} ${circ}`} strokeLinecap="round" style={{transition:"stroke-dasharray 0.5s ease"}}/>
      </svg>
      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:18}}>{emoji}</div>
    </div>
    <div style={{fontSize:22,fontWeight:900,color,lineHeight:1}}>{score}</div>
    <div style={{fontSize:11,color:"#888",fontWeight:600}}>{label}</div>
  </div>);
}

function MacroBar({label,value,min,max,color}){
  const pct=Math.min(Math.max(value,0),100);
  const tolerance=0.3; // סטייה של פחות מ-0.3% = "בסדר"
  const ok=value>=(min-tolerance)&&value<=(max+tolerance);
  const warn=!ok&&(value>=(min-1.5)&&value<=(max+1.5)); // קרוב אבל חורג קצת
  return(<div style={{marginBottom:10}}>
    <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:3,fontWeight:600}}>
      <span style={{color:"#444"}}>{label}</span>
      <span style={{color:ok?"#2D8A4E":warn?"#F59E0B":"#E8394D"}}>{value.toFixed(1)}%<span style={{color:"#aaa",fontWeight:400}}> ({min}–{max}%)</span></span>
    </div>
    <div style={{background:"#eee",borderRadius:8,height:10,overflow:"hidden",position:"relative"}}>
      <div style={{position:"absolute",left:`${min}%`,width:`${max-min}%`,background:"rgba(45,138,78,0.15)",height:"100%",borderRadius:8}}/>
      <div style={{width:`${pct}%`,background:ok?color:warn?"#F59E0B":"#E8394D",height:"100%",borderRadius:8,transition:"width 0.4s ease"}}/>
    </div>
  </div>);
}

function SuggCard({s,onFix}){
  const st={success:{bg:"#e8f8ee",border:"#2D8A4E",icon:"✅"},danger:{bg:"#fff0f0",border:"#E8394D",icon:"⛔"},warn:{bg:"#fff8e1",border:"#F59E0B",icon:"💡"},info:{bg:"#EDE9FE",border:"#7C3AED",icon:"ℹ️"}}[s.sev]||{bg:"#fff8e1",border:"#F59E0B",icon:"💡"};
  return(<div style={{padding:"10px 13px",borderRadius:10,marginBottom:6,fontSize:13,background:st.bg,borderRight:`4px solid ${st.border}`,color:"#333",lineHeight:1.5,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
    <span>{st.icon} {s.text}</span>
    {onFix&&s.fixType&&<button onClick={onFix} style={{flexShrink:0,padding:"4px 10px",borderRadius:8,border:"none",cursor:"pointer",background:st.border,color:"#fff",fontWeight:700,fontSize:11,fontFamily:"inherit",whiteSpace:"nowrap"}}>+ הוסף</button>}
  </div>);
}

function IngredientRow({ing,amount,onChange,onRemove}){
  return(<div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:amount>0?"rgba(255,255,255,0.95)":"rgba(255,255,255,0.45)",borderRadius:10,marginBottom:5,border:amount>0?"1.5px solid #D4A855":"1.5px solid transparent",transition:"all 0.2s"}}>
    <div style={{flex:1}}>
      <div style={{fontSize:13,fontWeight:amount>0?700:400,color:"#333"}}>{ing.name}</div>
      {ing.abv&&<div style={{fontSize:10,color:"#7C3AED",fontWeight:700}}>{ing.abv}% אלכ'</div>}
      {ing.protein>0&&amount>0&&<div style={{fontSize:10,color:"#0EA5E9",fontWeight:700}}>{((ing.protein/100)*amount).toFixed(1)}גר' חלבון</div>}
      {ing.note&&amount>0&&<div style={{fontSize:11,color:"#7C3AED",marginTop:2,lineHeight:1.4}}>ℹ️ {ing.note}</div>}
    </div>
    <input type="number" min={0} max={1000} step={5} value={amount > 0 ? amount : ""} placeholder="0" onChange={e=>onChange(ing.id, parseFloat(e.target.value) || 0)} style={{width:65,padding:"5px 6px",borderRadius:8,border:"1.5px solid #ddd",fontSize:14,textAlign:"center",background:"#fff",outline:"none",fontFamily:"inherit"}}/>
    <span style={{fontSize:11,color:"#888",width:26,textAlign:"right"}}>{ing.unit}</span>
    {onRemove&&<button onClick={onRemove} style={{background:"none",border:"none",cursor:"pointer",color:"#ccc",fontSize:16,padding:"0 2px"}}>×</button>}
  </div>);
}

function CupMeter({filled,container}){
const filledNum = parseFloat(filled) || 0;
const pct=Math.min(filledNum/container.ml*100,100), ok=filledNum>=container.minFill&&filledNum<=container.maxFill, over=filledNum>container.maxFill;
  const color=over?"#E8394D":ok?"#2D8A4E":"#F59E0B";
  return(<div style={{background:"#fff",borderRadius:16,padding:"12px 16px",marginBottom:14,boxShadow:"0 2px 10px rgba(0,0,0,0.07)",border:`1.5px solid ${color}`,position:"sticky",top:0,zIndex:100}}>
    <div style={{display:"flex",alignItems:"center",gap:12}}>
      <div style={{position:"relative",width:38,height:48,flexShrink:0}}>
        <svg width={38} height={48} viewBox="0 0 38 48"><path d="M4 3 L6 45 L32 45 L34 3 Z" fill="none" stroke="#ddd" strokeWidth={2}/><clipPath id="cc"><path d="M4 3 L6 45 L32 45 L34 3 Z"/></clipPath><rect x={0} y={48-pct*0.45} width={38} height={48} fill={color} opacity={0.2} clipPath="url(#cc)"/><rect x={0} y={48-pct*0.45} width={38} height={2} fill={color} opacity={0.6} clipPath="url(#cc)"/></svg>
      </div>
      <div style={{flex:1}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
          <span style={{fontWeight:800,fontSize:14}}>{filledNum.toFixed(0)}{"גר' / "}{container.ml}{"מ\"ל"}</span>
          <span style={{padding:"2px 10px",borderRadius:18,fontSize:12,fontWeight:700,background:ok?"#e8f8ee":over?"#fff0f0":"#fff8e1",color:ok?"#2D8A4E":over?"#E8394D":"#856404",border:`1.5px solid ${color}`}}>
            {over?"\u26a0\ufe0f \u05d9\u05d5\u05ea\u05e8 \u05de\u05d3\u05d9":ok?"\u2705 \u05de\u05d5\u05db\u05df":`\u05e2\u05d5\u05d3 ${(container.maxFill-filledNum).toFixed(0)}\u05d2\u05e8'`}
          </span>
        </div>
        <div style={{background:"#eee",borderRadius:8,height:8,overflow:"hidden",position:"relative"}}>
          <div style={{position:"absolute",left:`${container.minFill/container.ml*100}%`,width:`${(container.maxFill-container.minFill)/container.ml*100}%`,background:"rgba(45,138,78,0.15)",height:"100%"}}/>
          <div style={{width:`${Math.min(pct,100)}%`,background:color,height:"100%",borderRadius:8,transition:"width 0.3s"}}/>
        </div>
      </div>
    </div>
  </div>);
}

function InlineCustomForm({onAdd, container}){
  const [open,setOpen]=useState(false);
  const [name,setName]=useState(""),[fat,setFat]=useState(""),[sugar,setSugar]=useState(""),[protein,setProt]=useState(""),[abv,setAbv]=useState(""),[unit,setUnit]=useState("גרם"),[err,setErr]=useState("");
const [isCommercialProtein, setIsCommercialProtein] = useState(false);

  // מים מחושב אוטומטית
  const f=parseFloat(fat)||0, s=parseFloat(sugar)||0, p=parseFloat(protein)||0;
  const autoWater = Math.max(0, Math.round((100 - f - s - p) * 10) / 10);

  const handleAdd=()=>{
    if(!name.trim()){setErr("כנסי שם");return;}
    if(f+s+p>100){setErr("הסכום עובר 100% – בדקי את הערכים");return;}
    const abvNum=parseFloat(abv)||0;
   onAdd({id:"custom_"+Date.now(),name:name.trim(),fat:f,sugar:s,water:autoWater,protein:p,unit,custom:true,...(abvNum>0?{abv:abvNum}:{}),...(isCommercialProtein?{isCommercialProteinBase:true}:{})});
    setName("");setFat("");setSugar("");setProt("");setAbv("");setErr("");setIsCommercialProtein(false);setOpen(false);
  };
  if(!open) return <button onClick={()=>setOpen(true)} style={{width:"100%",padding:"9px",borderRadius:10,border:"2px dashed #C4B5FD",background:"rgba(237,233,254,0.3)",color:"#7C3AED",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",marginTop:4}}>+ הוסף מרכיב משלי</button>;
  return(<div style={{background:"#F5F3FF",borderRadius:12,padding:12,marginTop:6,border:"1.5px solid #C4B5FD"}}>
    <div style={{fontWeight:700,fontSize:12,color:"#7C3AED",marginBottom:8}}>הוספת מרכיב חדש</div>
    <input value={name} onChange={e=>setName(e.target.value)} placeholder="שם המרכיב..." style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1.5px solid #DDD6FE",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box",marginBottom:8}}/>

    {/* הוראה */}
    <div style={{fontSize:11,color:"#7C3AED",marginBottom:8,padding:"6px 10px",background:"rgba(124,58,237,0.06)",borderRadius:8,lineHeight:1.6}}>
      {"📋 כולל פירות! בדקי ערכים ל-100גר'/מ\"ל בגוגל (\"ערכי תזונה X ל-100 גרם\"). מים יחושבו אוטומטית."}
    </div>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
      {[
        ["🧈 שומן", fat, setFat, "מהתווית"],
        ["🍬 סוכרים", sugar, setSugar, 'מ"מהם סוכרים"'],
        ["💪 חלבון", protein, setProt, "מהתווית"],
      ].map(([lbl,val,setter,hint])=>(
        <div key={lbl}>
          <div style={{fontSize:10,color:"#7C3AED",marginBottom:2,textAlign:"center",fontWeight:600}}>{lbl}%</div>
          <div style={{fontSize:9,color:"#aaa",marginBottom:3,textAlign:"center"}}>{hint}</div>
          <input type="number" min={0} max={100} step={0.5} value={val} onChange={e=>setter(e.target.value)} placeholder="0"
            style={{width:"100%",padding:"7px 4px",borderRadius:7,border:"1.5px solid #DDD6FE",fontSize:14,textAlign:"center",fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
        </div>
      ))}
    </div>

    {/* מים אוטומטי */}
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,padding:"7px 10px",background:"#EFF6FF",borderRadius:8,border:"1px solid #BFDBFE"}}>
      <span style={{fontSize:12,color:"#2563EB"}}>💧 מים (אוטומטי):</span>
      <span style={{fontWeight:800,fontSize:14,color:"#2563EB"}}>{autoWater}%</span>
      <span style={{fontSize:10,color:"#93C5FD"}}>= 100 − שומן − סוכרים − חלבון</span>
    </div>
    {/* שדה אלכוהול */}
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,padding:"8px 10px",background:"rgba(124,58,237,0.06)",borderRadius:8,border:"1px solid #DDD6FE"}}>
      <span style={{fontSize:12,color:"#7C3AED",fontWeight:600,whiteSpace:"nowrap"}}>🍸 אלכוהול (ABV%)</span>
      <input type="number" min={0} max={100} step={1} value={abv} onChange={e=>setAbv(e.target.value)} placeholder="0"
        style={{width:60,padding:"5px 6px",borderRadius:7,border:"1.5px solid #DDD6FE",fontSize:13,textAlign:"center",fontFamily:"inherit",outline:"none"}}/>
      <span style={{fontSize:10,color:"#aaa",lineHeight:1.4}}>אחוז אלכוהול בבקבוק. למשל: וויסקי = 40%, יין = 12%</span>
    </div>
    {abv>0&&(()=>{
      const abvNum=parseFloat(abv)||0;
      if(!abvNum) return null;
      const containerSize = container?.maxFill || 500;
      const targetAmt = Math.round((1.5/100 * containerSize) / (abvNum/100));
      const maxAmt    = Math.round((2.0/100 * containerSize) / (abvNum/100));
      return <div style={{fontSize:11,color:"#7C3AED",marginBottom:8,padding:"6px 8px",background:"rgba(124,58,237,0.06)",borderRadius:6,lineHeight:1.6}}>
        <div>🎯 <strong>מומלץ (1.5% אתנול) לכוס {containerSize}מ"ל:</strong> {targetAmt}מ"ל</div>
        <div>⚠️ <strong>מקסימום (2% אתנול):</strong> {maxAmt}מ"ל (מעבר לזה לא יקפא)</div>
      </div>;
    })()}
    <div onClick={()=>setIsCommercialProtein(p=>!p)} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,padding:"8px 10px",background:"rgba(14,165,233,0.06)",borderRadius:8,border:"1.5px solid #BAE6FD",cursor:"pointer"}}>
      <div style={{width:18,height:18,borderRadius:4,border:"2px solid #0EA5E9",background:isCommercialProtein?"#0EA5E9":"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        {isCommercialProtein&&<span style={{color:"#fff",fontSize:13,lineHeight:1}}>✓</span>}
      </div>
      <span style={{fontSize:11,color:"#0369A1",lineHeight:1.4}}>💪 מעדן חלבון מסחרי / יוגורט חלבון מועשר (דנונה PRO, GO וכו') – המים שלו כבר קשורים</span>
    </div>
    {err&&<div style={{fontSize:11,color:"#E8394D",marginBottom:6}}>⚠️ {err}</div>}
    <div style={{display:"flex",gap:6}}>
      <select value={unit} onChange={e=>setUnit(e.target.value)} style={{padding:"7px 8px",borderRadius:8,border:"1.5px solid #DDD6FE",fontSize:12,fontFamily:"inherit",outline:"none",background:"#fff"}}><option value="גרם">גרם</option><option value='מ"ל'>מ"ל</option></select>
      <button onClick={handleAdd} style={{flex:1,padding:"8px",borderRadius:8,border:"none",cursor:"pointer",background:"#7C3AED",color:"#fff",fontWeight:700,fontSize:13,fontFamily:"inherit"}}>הוסף ✓</button>
      <button onClick={()=>{setOpen(false);setErr("");}} style={{padding:"8px 10px",borderRadius:8,border:"none",cursor:"pointer",background:"#F3F4F6",color:"#666",fontSize:13,fontFamily:"inherit"}}>✕</button>
    </div>
    <div style={{fontSize:10,color:"#C4B5FD",textAlign:"center",marginTop:5}}>לא יודעת? חפשי את שם המוצר באינטרנט</div>
  </div>);
}

// ─── Build Tab ────────────────────────────────────────────────────────────────

function BuildTab({onUseRecipe,customIngs,setCustomIngs,container}){
  const [type,setType]=useState("dairy");
  const [chosen,setChosen]=useState([]);
  const [primaryId,setPrimaryId]=useState(null);
  const [buildMode,setBuildMode]=useState("manual");
  const [searchTerm,setSearchTerm]=useState("");
  const [openPreset,setOpenPreset]=useState(null);
  const [expanded,setExpanded]=useState({dairy:false,coconut:false,nuts:false,sweeteners:false,liquids:false,alcohol:false,mixins:false,fruits:true});
  const [result,setResult]=useState(null);
  const [openCustom,setOpenCustom]=useState(false);
  const [showAdded,setShowAdded]=useState(false);
  const allAvail=getAllIngredients(customIngs);
  const toggle=id=>setChosen(prev=>{
    if(prev.includes(id)){const next=prev.filter(x=>x!==id);if(primaryId===id)setPrimaryId(null);return next;}
    return [...prev,id];
  });

  
  

  const HIGH_FAT=["cream38","coconutOil","almondButter","hazelnutButter","peanutButter","nutButter","pistachioPure","pistachioSweet","coconutCream"];
  const WATERY=["milk3","regularYogurt","greekYogurt","greekYogurt10","cookingCream","coconutMilk","almondMilk","soyMilk","hazelnutMilk","oatMilk",...FRUIT_IDS,...LIQUID_IDS];
  const chosenFat=chosen.filter(id=>HIGH_FAT.includes(id));
  const fatWarning=chosenFat.length>0&&!chosen.some(id=>WATERY.includes(id));

  const resultMacros=result?calcMacros(result,customIngs):null;
  const resultScore=resultMacros?calcScore(resultMacros,type,result):0;
  const resultSuggs=resultMacros?generateSuggestions(resultMacros,result,type,customIngs):[];
  const target=getTarget(type,result||{});
  const autoAdded=result?Object.entries(result).filter(([id,v])=>v>0&&!chosen.includes(id)):[];

  return(<div>

    {/* כותרת */}
    <div style={{background:"linear-gradient(135deg,#1a1a2e,#2d1b69)",borderRadius:16,padding:16,marginBottom:16,color:"#fff"}}>
      <div style={{fontSize:16,fontWeight:900}}>✨ בנה לי מתכון</div>
    </div>

    {/* מצב ב: בחירה ידנית */}
    {buildMode==="manual"&&(<>
      {/* סוג גלידה */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
        {ICE_CREAM_TYPES.map(t=>(
          <button key={t.id} onClick={()=>setType(t.id)} style={{padding:"7px 12px",borderRadius:20,border:"none",cursor:"pointer",fontFamily:"inherit",background:type===t.id?`linear-gradient(135deg,${t.color},#fff)`:"#fff",boxShadow:type===t.id?`0 0 0 2px ${t.accent}`:"0 1px 4px rgba(0,0,0,0.06)",fontSize:12,fontWeight:type===t.id?800:500,color:type===t.id?t.accent:"#555"}}>{t.label}</button>
        ))}
      </div>

    {/* מרכיבים נבחרים + בחירת ראשי */}
    <div style={{fontSize:13,fontWeight:700,color:"#444",marginBottom:chosen.length>0?6:10}}>
      בחר מרכיבים {chosen.length>0&&<span style={{background:"#D4A855",color:"#1a1a2e",borderRadius:10,padding:"1px 8px",fontSize:11,fontWeight:800,marginRight:6}}>{chosen.length}</span>}
    </div>
    {chosen.length>0&&(
      <div style={{marginBottom:12}}>
        {/* הסבר קצר */}
        <div style={{fontSize:11,color:"#888",marginBottom:7,padding:"6px 10px",background:"rgba(255,255,255,0.7)",borderRadius:10,lineHeight:1.6,border:"1px solid #eee"}}>
          💡 <strong>⭐ מרכיב ראשי</strong> = יקבל יותר נפח בגלידה. לחץ ⭐ שוב להסרה → חלוקה שווה בין כל המרכיבים
        </div>
        {/* chips */}
        <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"8px",background:"rgba(255,255,255,0.6)",borderRadius:12,border:"1px solid #e8e8e8"}}>
          {chosen.map(id=>{
            const ing=allAvail.find(i=>i.id===id); if(!ing) return null;
            const isPrimary=primaryId===id;
            return(<div key={id} style={{display:"flex",alignItems:"center",gap:3,padding:"5px 8px 5px 5px",borderRadius:16,background:isPrimary?"#1a1a2e":"#f0f0f0",border:isPrimary?"2px solid #D4A855":"2px solid transparent",transition:"all 0.15s"}}>
              <button onClick={()=>setPrimaryId(isPrimary?null:id)} title={isPrimary?"הסר כוכב – חלוקה שווה":"סמן כמרכיב ראשי"} style={{background:"none",border:"none",cursor:"pointer",fontSize:14,padding:"0 2px",lineHeight:1}}>{isPrimary?"⭐":"☆"}</button>
              <span style={{fontSize:12,fontWeight:isPrimary?700:500,color:isPrimary?"#D4A855":"#444"}}>{ing.name}</span>
              <button onClick={()=>toggle(id)} style={{background:"none",border:"none",cursor:"pointer",color:isPrimary?"#666":"#bbb",fontSize:13,padding:"0 2px",lineHeight:1}}>×</button>
            </div>);
          })}
        </div>
        {!primaryId&&chosen.length>1&&(
          <div style={{fontSize:10,color:"#aaa",marginTop:5,textAlign:"center"}}>
            חלוקה שווה – כל מרכיב יקבל נפח דומה
          </div>
        )}
      </div>
    )}
    {Object.entries(INGREDIENTS).map(([cat,ings])=>{
      const inCat=ings.filter(i=>chosen.includes(i.id)).length, open=expanded[cat];
      return(<div key={cat} style={{marginBottom:7}}>
        <button onClick={()=>setExpanded(p=>({...p,[cat]:!p[cat]}))} style={{width:"100%",textAlign:"right",background:open?"#1a1a2e":"rgba(255,255,255,0.7)",color:open?"#fff":"#444",border:"none",borderRadius:open?"12px 12px 0 0":"12px",padding:"9px 13px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span>{CATEGORY_LABELS[cat]}</span>
          <span style={{display:"flex",alignItems:"center",gap:6}}>{inCat>0&&<span style={{background:"#D4A855",color:"#1a1a2e",borderRadius:10,padding:"1px 7px",fontSize:11,fontWeight:800}}>{inCat}</span>}<span>{open?"▲":"▼"}</span></span>
        </button>
        {open&&(<div style={{background:"rgba(255,255,255,0.55)",borderRadius:"0 0 12px 12px",padding:"8px 8px 4px",display:"flex",flexWrap:"wrap",gap:6}}>
          {ings.map(ing=>{const sel=chosen.includes(ing.id);return(<button key={ing.id} onClick={()=>toggle(ing.id)} style={{padding:"6px 11px",borderRadius:18,border:"none",cursor:"pointer",fontFamily:"inherit",background:sel?"#1a1a2e":"#fff",color:sel?"#D4A855":"#444",fontWeight:sel?700:400,fontSize:12,boxShadow:sel?"0 0 0 1.5px #D4A855":"0 1px 4px rgba(0,0,0,0.07)",transition:"all 0.12s"}}>{sel?"✓ ":""}{ing.name}</button>);})}
        </div>)}
      </div>);
    })}
    {/* מרכיב מותאם אישי */}
    <div style={{marginBottom:7}}>
      <button onClick={()=>setOpenCustom(p=>!p)} style={{width:"100%",textAlign:"right",background:openCustom?"#7C3AED":"rgba(255,255,255,0.7)",color:openCustom?"#fff":"#7C3AED",border:`2px dashed ${openCustom?"transparent":"#C4B5FD"}`,borderRadius:openCustom?"12px 12px 0 0":"12px",padding:"9px 13px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span>🧪 מרכיב משלי {customIngs.length>0&&`(${customIngs.length})`}</span>
        <span style={{display:"flex",alignItems:"center",gap:6}}>
          {customIngs.filter(i=>chosen.includes(i.id)).length>0&&<span style={{background:"#D4A855",color:"#1a1a2e",borderRadius:10,padding:"1px 7px",fontSize:11,fontWeight:800}}>{customIngs.filter(i=>chosen.includes(i.id)).length}</span>}
          <span>{openCustom?"▲":"▼"}</span>
        </span>
      </button>
      {openCustom&&(
        <div style={{background:"rgba(237,233,254,0.5)",borderRadius:"0 0 12px 12px",padding:"8px 8px 6px",border:"2px dashed #C4B5FD",borderTop:"none"}}>
          {customIngs.length>0&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
              {customIngs.map(ing=>{const sel=chosen.includes(ing.id);return(
                <button key={ing.id} onClick={()=>toggle(ing.id)} style={{padding:"6px 11px",borderRadius:18,border:"none",cursor:"pointer",fontFamily:"inherit",background:sel?"#7C3AED":"#fff",color:sel?"#fff":"#7C3AED",fontWeight:sel?700:400,fontSize:12,boxShadow:sel?"0 0 0 1.5px #7C3AED":"0 1px 4px rgba(0,0,0,0.07)"}}>
                  {sel?"✓ ":""}{ing.name}
                </button>
              );})}
            </div>
          )}
          <InlineCustomForm onAdd={ing=>setCustomIngsAndSave(prev=>[...prev,ing])} container={container}/>
        </div>
      )}
    </div>

    {fatWarning&&(<div style={{background:"#fff8e1",border:"2px solid #F59E0B",borderRadius:12,padding:"10px 13px",marginBottom:12}}>
      <div style={{fontWeight:800,fontSize:12,color:"#92400E",marginBottom:3}}>⚠️ חסר בסיס מימי</div>
      <div style={{fontSize:12,color:"#78350F",lineHeight:1.5}}>{chosenFat.map(id=>allAvail.find(i=>i.id===id)?.name).join(', ')} – מרכיבים שומניים. כדאי לשלב עם חלב, יוגורט, חלב צמחי, או פירות</div>
    </div>)}

    <button onClick={()=>{if(chosen.length>0){const r=buildRecipe(chosen,type,customIngs,container.maxFill,primaryId);setResult(r);setShowAdded(false);}}} disabled={chosen.length===0} style={{width:"100%",padding:"13px",borderRadius:14,border:"none",cursor:chosen.length>0?"pointer":"not-allowed",fontFamily:"inherit",background:chosen.length>0?"linear-gradient(120deg,#D4A855,#c08a30)":"#ddd",color:chosen.length>0?"#1a1a2e":"#aaa",fontWeight:900,fontSize:16,boxShadow:chosen.length>0?"0 4px 16px rgba(212,168,85,0.35)":"none",marginBottom:16,transition:"all 0.2s"}}>⚡ חשב כמויות מאוזנות</button>
    </>)}

    {result&&resultMacros&&(()=>{
      const chosenMacros = calcMacros(
        Object.fromEntries(Object.entries(result).filter(([id])=>chosen.includes(id))),
        customIngs
      );
      const addedMacros = autoAdded.length>0 ? calcMacros(
        Object.fromEntries(autoAdded.filter(([,v])=>v>0)),
        customIngs
      ) : null;
      const t = TARGETS[type]||TARGETS.dairy;

      return(<div style={{background:"#fff",borderRadius:18,padding:18,boxShadow:"0 4px 20px rgba(0,0,0,0.1)",border:"2px solid #D4A855"}}>

        {/* כותרת + ציון */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontWeight:900,fontSize:16}}>🍦 המתכון שלך</div>
          <ScoreRing score={resultScore}/>
        </div>

        {/* סקשן 1: מה שבחרת */}
        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:800,color:"#666",marginBottom:7,textTransform:"uppercase",letterSpacing:0.5}}>המרכיבים שבחרת</div>
          {Object.entries(result).filter(([id,v])=>chosen.includes(id)&&v>0).map(([id,amt])=>{
            const ing=allAvail.find(i=>i.id===id); if(!ing) return null;
            return(<div key={id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",borderRadius:10,marginBottom:5,background:"rgba(255,255,255,0.7)",border:"1.5px solid #e0e0e0"}}>
              <span style={{fontSize:13,fontWeight:700,color:"#333"}}>{ing.name}</span>
              <span style={{fontWeight:900,fontSize:14,color:"#1a1a2e"}}>{amt} <span style={{fontSize:11,color:"#888",fontWeight:400}}>{ing.unit}</span></span>
            </div>);
          })}
          {chosenMacros&&(
            <div style={{display:"flex",gap:8,fontSize:11,color:"#888",padding:"5px 8px",background:"#f9f9f9",borderRadius:8,marginTop:4}}>
              <span>ש {chosenMacros.fat.toFixed(1)}%</span>
              <span>·</span>
              <span>ס {chosenMacros.sugar.toFixed(1)}%</span>
              <span>·</span>
              <span>מ {chosenMacros.water.toFixed(1)}%</span>
              <span style={{marginRight:"auto",color:"#bbb"}}>{chosenMacros.weight.toFixed(0)}גר'</span>
            </div>
          )}
        </div>

        {/* סקשן 2: מה המחשבון הוסיף */}
        {autoAdded.length>0&&(
          <div style={{marginBottom:12}}>
            <button onClick={()=>setShowAdded(p=>!p)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",borderRadius:showAdded?"10px 10px 0 0":"10px",background:"#FEF3C7",border:"1.5px solid #F59E0B",borderBottom:showAdded?"none":"1.5px solid #F59E0B",cursor:"pointer",fontFamily:"inherit"}}>
              <span style={{fontSize:12,fontWeight:700,color:"#92400E"}}>⚖️ המחשבון הוסיף לאיזון ({autoAdded.length})</span>
              <span style={{fontSize:11,color:"#B45309"}}>{showAdded?"▲":"▼ פירוט"}</span>
            </button>
            {showAdded&&(
              <div style={{background:"#fffbeb",border:"1.5px solid #F59E0B",borderTop:"none",borderRadius:"0 0 10px 10px",padding:"8px 12px 6px"}}>
                {autoAdded.map(([id,amt])=>{
                  const ing=allAvail.find(i=>i.id===id)||{name:id,unit:"גרם",fat:0,sugar:0,water:0};
                  const reason=SWEETENER_IDS.includes(id)||id==="sugar"
                    ?`כדי להגיע ל-${t.sugar[0]}–${t.sugar[1]}% סוכר (עכשיו ${chosenMacros?.sugar.toFixed(1)}%)`
                    :[...LIQUID_IDS,"water"].includes(id)
                    ?`כדי להגיע ל-${t.water[0]}–${t.water[1]}% מים (עכשיו ${chosenMacros?.water.toFixed(1)}%)`
                    :`כדי להגיע ל-${t.fat[0]}–${t.fat[1]}% שומן (עכשיו ${chosenMacros?.fat.toFixed(1)}%)`;
                  return(<div key={id} style={{padding:"7px 0",borderBottom:"1px solid #FDE68A"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{fontSize:13,fontWeight:700,color:"#92400E"}}>{ing.name}</span>
                      <span style={{fontWeight:800,fontSize:14,color:"#92400E"}}>{amt} <span style={{fontSize:11,fontWeight:400,color:"#B45309"}}>{ing.unit}</span></span>
                    </div>
                    <div style={{fontSize:11,color:"#B45309",marginTop:2}}>↳ {reason}</div>
                  </div>);
                })}
                {addedMacros&&(
                  <div style={{display:"flex",gap:8,fontSize:11,color:"#B45309",padding:"5px 0 2px",marginTop:4}}>
                    <span>ש {addedMacros.fat.toFixed(1)}%</span><span>·</span>
                    <span>ס {addedMacros.sugar.toFixed(1)}%</span><span>·</span>
                    <span>מ {addedMacros.water.toFixed(1)}%</span>
                    <span style={{marginRight:"auto",color:"#D4A855"}}>{addedMacros.weight.toFixed(0)}גר'</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* סקשן 3: תוצאה סופית */}
        <div style={{background:"linear-gradient(135deg,#f8f4ed,#fff)",borderRadius:12,padding:"12px 14px",border:"1.5px solid #e8d5a0",marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:800,color:"#D4A855",marginBottom:8,textTransform:"uppercase",letterSpacing:0.5}}>✨ הגלידה הסופית</div>
          <MacroBar label="שומן"  value={resultMacros.fat}   min={target.fat[0]}   max={target.fat[1]}   color="#D4A855"/>
          <MacroBar label="סוכר"  value={resultMacros.sugar} min={target.sugar[0]} max={target.sugar[1]} color="#E8394D"/>
          <MacroBar label="% מים" value={resultMacros.water} min={target.water[0]} max={target.water[1]} color="#2563EB"/>
          <div style={{fontSize:12,color:"#aaa",textAlign:"center",marginTop:6}}>
            סה"כ: {resultMacros.weight.toFixed(0)}גר' {resultMacros.weight>=container.minFill&&resultMacros.weight<=container.maxFill?`✅ מתאים ל-${container.label}`:"⚠️ בדוק נפח"}
          </div>
        </div>

        {resultSuggs.map((s,i)=><SuggCard key={i} s={s}/>)}
        <button onClick={()=>onUseRecipe(result,type)} style={{width:"100%",padding:"11px",borderRadius:12,border:"none",cursor:"pointer",background:"linear-gradient(120deg,#1a1a2e,#0f3460)",color:"#D4A855",fontWeight:800,fontSize:14,fontFamily:"inherit",marginTop:8}}>📥 העבר למחשבון לעריכה</button>
      </div>);
    })()}
  </div>);
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function NinjaCreamiApp(){
  const [typeOverride,setTypeOverride] = useState(null);
  const [amounts,setAmounts]   = useState({});
  const [containerIdx,setContainerIdx] = useState(0);
  const [expanded,setExpanded] = useState({dairy:false,sweeteners:false,liquids:false,alcohol:false,fruits:false,coconut:false,nuts:false,mixins:false,_custom:false});
  const [tab,setTab]           = useState("calc");
  const [recipes,setRecipes]   = useState([]);
  const [showWelcome,setShowWelcome] = useState(()=>{
    try { return !localStorage.getItem("ninja_visited"); } catch(e){ return true; }
  });
  const [accessData, setAccessData] = useState({ hasAccess: true, loading: true });

  useEffect(() => {
    const checkUserAccess = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('is_subscribed, created_at')
            .eq('id', user.id)
            .single();

          if (profile) {
            const signupDate = new Date(profile.created_at);
            const now = new Date();
            const diffInDays = (now - signupDate) / (1000 * 60 * 60 * 24);
            const isAllowed = profile.is_subscribed === true || diffInDays <= 3;
            setAccessData({ hasAccess: isAllowed, loading: false });
          } else {
            setAccessData({ hasAccess: true, loading: false });
          }
        } else {
          setAccessData({ hasAccess: true, loading: false });
        }
      } catch (error) {
        setAccessData({ hasAccess: true, loading: false });
      }
    };
    checkUserAccess();
  }, []);
  const [recipeName,setRecipeName]=useState("");
  const [recipeNote,setRecipeNote]=useState("");
  const [showSave,setShowSave] = useState(false);
  const [openRecipeId, setOpenRecipeId] = useState(null);
  const [customIngs,setCustomIngs]=useState(()=>{
    try { const s=localStorage.getItem("ninja_custom_ings"); return s?JSON.parse(s):[]; } catch(e){ return []; }
  });
  const setCustomIngsAndSave = async (updater) => {
    // 1. קודם כל מעדכנים את המסך (שיהיה מהיר למשתמש)
    const nextIngs = typeof updater === 'function' ? updater(customIngs) : updater;
    setCustomIngs(nextIngs);
    localStorage.setItem("ninja_custom_ings", JSON.stringify(nextIngs));

    // 2. עכשיו מנסים לשלוח לענן של Supabase
    try {
      // בודקים אם יש משתמש מחובר
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log("לא מחובר - נשמר מקומית בלבד");
        return;
      }

      // לוקחים את המרכיב האחרון שנוסף לרשימה
      const lastIng = nextIngs[nextIngs.length - 1];
      if (!lastIng) return;

      // שליחה לטבלה custom_ingredients
      const { error } = await supabase
        .from('custom_ingredients')
        .insert([{
  custom_id: lastIng.id || lastIng.name,
  name: lastIng.name,
  unit: lastIng.unit || 'גר\'',
  fat: parseFloat(lastIng.fat) || 0,
  sugar: parseFloat(lastIng.sugar) || 0,
  water: parseFloat(lastIng.water) || 0,
  protein: parseFloat(lastIng.protein) || 0,
  alcohol: parseFloat(lastIng.alcohol || lastIng.abv) || 0,
  calories: parseFloat(lastIng.calories) || 0,
  carbs: parseFloat(lastIng.carbs) || 0,
  is_commercial_protein_base: lastIng.isCommercialProteinBase || false,
  user_id: user.id
}]);

      if (error) throw error;
      console.log("סונכרן לסופהבייס בהצלחה! ✨");

    } catch (err) {
      console.error("שגיאה בסנכרון:", err.message);
    }
  };
  const [isSaving, setIsSaving] = useState(false);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [allRecipes, setAllRecipes] = useState([]);
  const deleteIngredient = async (index) => {
    const ingToDelete = customIngs[index];
    if (!ingToDelete) return;

    // 1. מחיקה מהירה מהמסך ומלוקאל סטורג'
    const nextIngs = customIngs.filter((_, i) => i !== index);
    setCustomIngs(nextIngs);
    localStorage.setItem("ninja_custom_ings", JSON.stringify(nextIngs));

    // 2. מחיקה מהסופהבייס
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = supabase.from('custom_ingredients').delete();

      // אם ה-ID הוא מספר (ביג-אינט), נשתמש בו. אם זה טקסט של קאסטום, נמחק לפי שם.
      const isRealId = ingToDelete.id && !isNaN(ingToDelete.id) && typeof ingToDelete.id !== 'string';

      if (isRealId) {
        query = query.eq('id', Number(ingToDelete.id));
      } else {
        query = query.eq('name', ingToDelete.name);
      }

      // מוודאים שזה המשתמש הנכון
      const { error } = await query.eq('user_id', user.id);

      if (error) throw error;
      console.log("נמחק מהענן בהצלחה! ✨");
    } catch (err) {
      console.error("שגיאה במחיקה:", err.message);
    }
  };

  // פונקציה למשיכת מתכונים מהענן
 const fetchRecipes = useCallback(async () => {
  try {
    // משתמש מחובר? שלוף את המתכונים שלו
    const { data: { user } } = await supabase.auth.getUser();

    if (user) {
      const { data: myData, error: myError } = await supabase
        .from('saved_recipes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (!myError && myData) setSavedRecipes(myData);
    } else {
      setSavedRecipes([]);
    }

    // מתכוני קהילה – כל מה שis_public=true (של כולם)
    const { data: publicData, error: publicError } = await supabase
      .from('community_recipes')
.select('*')
      .order('created_at', { ascending: false });
    if (!publicError && publicData) setAllRecipes(publicData);

  } catch (err) {
    console.error("Error fetching:", err);
  }
}, []);
  // טעינה ראשונית של הרשימה
  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  
  

  const saveToCloud = async () => {
  if (!recipeName.trim()) return alert("אנא הזן שם למתכון");
  setIsSaving(true);
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      alert("יש להתחבר כדי לשמור מתכונים ☁️");
      setIsSaving(false);
      return;
    }

    const currentMacros = calcMacros(amounts, customIngs);   // ✅ הפונקציה הנכונה
    const { error } = await supabase.from('saved_recipes').insert([
      {
        name: recipeName,
        type: detectedType,                                   // ✅ הסוג שזוהה
        amounts: amounts,
        macros: currentMacros || {},
        user_id: user.id,
        is_public: false,
        notes: recipeNote,
      }
    ]);

      if (error) throw error;

      alert("🍦 המתכון נשמר בהצלחה בענן!");
      setRecipeName(''); 
      fetchRecipes();    
    } catch (err) {
      alert("שגיאה בשמירה: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const container=CONTAINER_SIZES[containerIdx];
  const detected   = detectType(amounts);
  const detectedType = typeOverride || detected.type;
  const typeInfo   = ICE_CREAM_TYPES.find(t=>t.id===detectedType) || {label:`🎨 ${detected.label}`, color:"#F5F5F5", accent:"#888"};

  const handleAmount=useCallback((id,val)=>setAmounts(prev=>({...prev,[id]:val})),[]);
  const toggleCat=cat=>setExpanded(prev=>({...prev,[cat]:!prev[cat]}));

  const macros  =calcMacros(amounts,customIngs);
  const target  =getTarget(detectedType,amounts);
  const score   =macros?calcScore(macros,detectedType,amounts):0;
  const suggs   =macros?generateSuggestions(macros,amounts,detectedType,customIngs):[];
  const totalW  =macros?.weight||0;
  const activeCt=Object.values(amounts).filter(v=>v>0).length;

  const handleAutoBalance=()=>setAmounts(autoBalance(amounts,detectedType==="mixed"?"dairy":detectedType,customIngs,container.maxFill));
  const handleSave=()=>{
    if(!recipeName.trim()||!macros) return;
    setRecipes(prev=>[{id:Date.now(),name:recipeName.trim(),note:recipeNote.trim(),type:detectedType,amounts:{...amounts},macros,score},...prev]);
    setRecipeName("");setRecipeNote("");setShowSave(false);
  };

  const TABS=[["calc","🧮 מחשבון"],["build","✨ בנה מתכון"],["recipes","📋 מתכונים"],["tips","💡 טיפים"],["community","🌍 קהילה"]];
  const [showTypeOverride,setShowTypeOverride]=useState(false);
  const [openCustomCalc,setOpenCustomCalc]=useState(false);

  if (!accessData.loading && !accessData.hasAccess) {
    return (
      <div style={{ 
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', backgroundColor: '#FEFDF5', direction: 'rtl', padding: '20px', textAlign: 'center'
      }}>
        <div style={{ 
          backgroundColor: 'white', padding: '30px', borderRadius: '40px', 
          boxShadow: '0 15px 40px rgba(0,0,0,0.08)', maxWidth: '550px', border: '2px solid #F6D69F'
        }}>
          {/* הלוגו המעודכן שלך */}
          <img 
            src="/landing-logo.jpeg" 
            alt="לוגו גלידה הכי טובה" 
            style={{ maxWidth: '280px', height: 'auto', marginBottom: '15px', borderRadius: '20px' }}
          />
          
          <h1 style={{ color: '#E91E63', fontSize: '28px', fontFamily: 'sans-serif', fontWeight: '900', marginBottom: '15px' }}>
            תקופת הניסיון הסתיימה
          </h1>
          
          <p style={{ color: '#555', fontSize: '18px', lineHeight: '1.6', marginBottom: '25px' }}>
  נהנית מהמחשבון יש גלידה הכי טובה? <br/>
  כדי להמשיך להשתמש בו ולשמור את המתכונים שלך, <br/>
  ניתן להצטרף למנוי באחת האפשרויות:
</p>

<div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginBottom: '20px' }}>
  {/* כפתור חודשי */}
  <button 
    onClick={() => window.location.href = 'https://smartbee.co.il/public-pages/?redirect-path=pay/69f44ce8574609580d39d753'}
    style={{
      backgroundColor: '#FEFDF5', border: '2px solid #F6D69F', padding: '15px', 
      borderRadius: '20px', cursor: 'pointer', flex: '1', maxWidth: '180px'
    }}>
    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>מנוי חודשי</div>
    <div style={{ fontSize: '24px', fontWeight: '900', color: '#E91E63' }}>₪6.90</div>
  </button>

  {/* כפתור שנתי */}
  <button 
   onClick={() => window.location.href = 'https://smartbee.co.il/public-pages/?redirect-path=pay/69f44d4f574609580d39dc54'}
    style={{
      backgroundColor: '#FFF0F5', border: '2px solid #E91E63', padding: '15px', 
      borderRadius: '20px', cursor: 'pointer', flex: '1', maxWidth: '180px', position: 'relative'
    }}>
    <div style={{ 
      position: 'absolute', top: '-10px', right: '10px', backgroundColor: '#E91E63', 
      color: 'white', fontSize: '10px', padding: '2px 8px', borderRadius: '10px' 
    }}>משתלם!</div>
    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>מנוי שנתי</div>
    <div style={{ fontSize: '24px', fontWeight: '900', color: '#E91E63' }}>₪69.90</div>
  </button>
</div>
          
          <div style={{ 
  backgroundColor: '#FFEB3B', color: '#1A1A2E', border: 'none', padding: '14px 40px',
  fontSize: '16px', borderRadius: '50px', fontWeight: 'bold', textAlign: 'center', marginTop: '10px'
}}>
  להפעלת המנוי — בחרי באחת האפשרויות למעלה ⬆️
</div>
        </div>
      </div>
    );
  }
  if(showWelcome) return(
    <div dir="rtl" style={{minHeight:"100vh",background:"linear-gradient(135deg,#1a1a2e,#16213e,#0f3460)",fontFamily:"'Segoe UI','Arial Hebrew',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:"32px 24px",maxWidth:380,width:"100%",textAlign:"center",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
        <div style={{fontSize:64,marginBottom:8}}>{"🍦"}</div>
        <h1 style={{fontSize:24,fontWeight:900,color:"#1a1a2e",margin:"0 0 8px"}}>Ninja Creami Calculator</h1>
        <p style={{fontSize:14,color:"#888",marginBottom:28,lineHeight:1.6}}>המחשבון החכם להכנת גלידה מושלמת בבית</p>

        <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:28,textAlign:"right"}}>
          {[
            {icon:"🥄",title:"\u05d4\u05d5\u05e1\u05d9\u05e4\u05d9 \u05de\u05e8\u05db\u05d9\u05d1\u05d9\u05dd",desc:"\u05d1\u05d7\u05e8\u05d9 \u05de\u05d4\u05e8\u05e9\u05d9\u05de\u05d4 \u05d5\u05db\u05d5\u05d5\u05e0\u05e0\u05d9 \u05d0\u05ea \u05d4\u05db\u05de\u05d5\u05d9\u05d5\u05ea"},
            {icon:"🎯",title:"\u05d4\u05de\u05d7\u05e9\u05d1\u05d5\u05df \u05de\u05d6\u05d4\u05d4 \u05d0\u05d5\u05d8\u05d5\u05de\u05d8\u05d9\u05ea",desc:"\u05e1\u05d5\u05d2 \u05d4\u05d2\u05dc\u05d9\u05d3\u05d4 \u05e0\u05e7\u05d1\u05e2 \u05dc\u05e4\u05d9 \u05d4\u05de\u05e8\u05db\u05d9\u05d1\u05d9\u05dd \u05e9\u05d1\u05d7\u05e8\u05ea"},
            {icon:"📊",title:"\u05e7\u05d1\u05dc\u05d9 \u05e0\u05d9\u05ea\u05d5\u05d7 \u05de\u05d9\u05d9\u05d3\u05d9",desc:"\u05e6\u05d9\u05d5\u05df, \u05d0\u05d7\u05d5\u05d6\u05d9 \u05de\u05d0\u05e7\u05e8\u05d5 \u05d5\u05d4\u05de\u05dc\u05e6\u05d5\u05ea \u05dc\u05ea\u05d9\u05e7\u05d5\u05df"},
          ].map(s=>(
            <div key={s.icon} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"12px 14px",borderRadius:14,background:"#FFF8EC",border:"1px solid #F5E6CA"}}>
              <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#D4A855,#C07A20)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{s.icon}</div>
              <div>
                <div style={{fontWeight:800,fontSize:13,color:"#1a1a2e",marginBottom:2}}>{s.title}</div>
                <div style={{fontSize:12,color:"#888"}}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={()=>{
          try{localStorage.setItem("ninja_visited","1");}catch(e){}
          setShowWelcome(false);
        }} style={{width:"100%",padding:"14px",borderRadius:14,border:"none",cursor:"pointer",background:"linear-gradient(135deg,#D4A855,#C07A20)",color:"#fff",fontWeight:900,fontSize:16,fontFamily:"inherit",boxShadow:"0 4px 16px rgba(212,168,85,0.4)"}}>
          {"🚀 בואי נתחיל!"}
        </button>

        <p style={{fontSize:11,color:"#ccc",marginTop:12}}>{"המסך הזה יופיע רק פעם אחת"}</p>
      </div>
    </div>
  );

  return(<div dir="rtl" style={{minHeight:"100vh",background:"linear-gradient(135deg,#FFF8EC,#FFF0D6,#FFE8C8)",fontFamily:"'Segoe UI','Arial Hebrew',sans-serif",color:"#2a2a2a"}}>

    {/* Header */}
    <div style={{background:"linear-gradient(120deg,#1a1a2e,#16213e,#0f3460)",color:"#fff",padding:"18px 18px 12px",boxShadow:"0 4px 20px rgba(0,0,0,0.18)"}}>
      <div style={{maxWidth:700,margin:"0 auto"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
          <span style={{fontSize:30}}>🍦</span>
          <div><h1 style={{margin:0,fontSize:20,fontWeight:900,letterSpacing:-0.5}}>Ninja Creami Calculator</h1><p style={{margin:0,fontSize:11,color:"#a0b4cc"}}>מחשבון מרכיבים לגלידה מושלמת</p></div>
        </div>
        <div style={{display:"flex",gap:7,marginBottom:10,alignItems:"center"}}>
          <span style={{fontSize:11,color:"#a0b4cc"}}>כוס:</span>
          {CONTAINER_SIZES.map((c,i)=>(<button key={c.ml} onClick={()=>{setContainerIdx(i);setAmounts({});}} style={{padding:"5px 12px",borderRadius:18,border:`1.5px solid ${containerIdx===i?"#D4A855":"rgba(255,255,255,0.2)"}`,cursor:"pointer",fontFamily:"inherit",background:containerIdx===i?"#D4A855":"transparent",color:containerIdx===i?"#1a1a2e":"#ddd",fontWeight:containerIdx===i?800:500,fontSize:12}}>{c.icon} {c.label}</button>))}
        </div>
        <div style={{display:"flex",gap:6}}>
          {TABS.map(([id,lbl])=>(<button key={id} onClick={()=>setTab(id)} style={{padding:"6px 13px",borderRadius:18,border:"none",cursor:"pointer",fontFamily:"inherit",background:tab===id?"rgba(212,168,85,0.2)":"transparent",color:tab===id?"#D4A855":"#ccc",fontWeight:tab===id?800:500,fontSize:12,borderBottom:tab===id?"2px solid #D4A855":"2px solid transparent"}}>{lbl}{id==="recipes"&&recipes.length>0?` (${recipes.length})`:""}</button>))}
        </div>
      </div>
    </div>

    <div style={{maxWidth:700,margin:"0 auto",padding:"16px 14px"}}>

      {/* Build Tab */}
      {tab==="build"&&<BuildTab onUseRecipe={(r,t)=>{setAmounts(r);setTypeOverride(t);setTab("calc");}} customIngs={customIngs} setCustomIngs={setCustomIngsAndSave} container={container}/>}

      {/* Recipes Tab */}
      {tab === "recipes" && (
        <div style={{ padding: "0 4px" }}>
          {savedRecipes.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#888", background: "#fff", borderRadius: 18 }}>
              <p>עדיין אין מתכונים שמורים... 🍦</p>
            </div>
          ) : (
            savedRecipes.map((r) => {
  const ti = ICE_CREAM_TYPES.find(t => t.id === r.type) || { label: r.type || "מעורב 🎨", accent: "#888" };
  const isOpen = openRecipeId === r.id;
  const allAvail = getAllIngredients(customIngs);
  return (
    <div key={r.id} style={{ background: "#fff", borderRadius: 18, marginBottom: 14, boxShadow: "0 4px 16px rgba(124,58,237,0.08)", border: "1px solid #f3e8ff", overflow: "hidden" }}>
      {/* שורה עליונה — תמיד גלויה */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#1a1a2e" }}>{r.name}</div>
          <div style={{ fontSize: 12, color: "#7C3AED", fontWeight: 600, marginTop: 2 }}>{ti.label}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={async (e) => { e.stopPropagation(); try { const { data: { user } } = await supabase.auth.getUser(); const { data: existingArr } = await supabase.from('community_recipes').select('id').eq('title', r.name).eq('user_id', user?.id); const existing = existingArr?.[0]; if (existing) { if(window.confirm("להסיר מהקהילה?")) { await supabase.from('community_recipes').delete().eq('id', existing.id); alert("הוסר"); fetchRecipes(); } } else { await supabase.from('community_recipes').insert([{ title: r.name, ingredients: r.amounts, score: r.score || 0, type: r.type, user_id: user?.id || null, notes: r.notes || "" }]); alert("שותף! 🎉"); fetchRecipes(); } } catch(e) { alert("שגיאה: " + e.message); } }} style={{ background: "#f1f5f9", color: "#64748b", border: "none", padding: "6px 10px", borderRadius: 10, cursor: "pointer", fontSize: 11, fontWeight: 700, textAlign: "center", fontFamily: "inherit" }}>
            🌍 שתף לקהילה
            <div style={{ fontSize: 8, color: "#94a3b8", marginTop: 1 }}>לחיצה נוספת תסיר</div>
          </button>
          <button onClick={e => { e.stopPropagation(); if(window.confirm("למחוק?")) { supabase.from('saved_recipes').delete().eq('id', r.id).then(() => fetchRecipes()); } }} style={{ background: "#fee2e2", color: "#ef4444", border: "none", padding: "6px 8px", borderRadius: 10, cursor: "pointer", fontSize: 12 }}>🗑️</button>
          <button onClick={() => setOpenRecipeId(isOpen ? null : r.id)} style={{ background: "#7C3AED", color: "#fff", border: "none", padding: "6px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>
            {isOpen ? "▲ סגור" : "▼ טען"}
          </button>
        </div>
      </div>
      {/* תוכן — נפתח בלחיצה על טען */}
      {isOpen && (
        <div style={{ padding: "0 16px 16px" }}>
          {r.amounts && Object.keys(r.amounts).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {Object.entries(r.amounts).map(([id, amt]) => {
                const ing = allAvail.find(i => i.id === id);
                if (!ing || !amt) return null;
                return (
                  <div key={id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13 }}>
                    <span style={{ color: "#333", fontWeight: 500 }}>{ing.name}</span>
                    <span style={{ fontWeight: 700, color: "#1a1a2e" }}>{amt} <span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>{ing.unit}</span></span>
                  </div>
                );
              })}
            </div>
          )}
          {r.notes && <div style={{ fontSize: 13, color: "#666", marginBottom: 12, padding: "8px 12px", background: "#f9f5ff", borderRadius: 10 }}>📝 {r.notes}</div>}
          <button onClick={() => { setAmounts(r.amounts || {}); setRecipeName(r.name); setTab("calc"); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ width: "100%", padding: "9px", borderRadius: 10, border: "none", cursor: "pointer", background: "linear-gradient(120deg,#D4A855,#c08a30)", color: "#1a1a2e", fontWeight: 800, fontSize: 13, fontFamily: "inherit" }}>⚡ בחר מתכון זה</button>
        </div>
      )}
    </div>
  );
})
          )}
        </div>
      )}
      {/* Tips Tab */}
      {tab==="tips"&&(<div>
        {/* חלבון */}
        <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontWeight:800,fontSize:14,marginBottom:12,color:"#1a1a2e"}}>{"💪 "}גלידות חלבון</div>
          {[
            ["\u05d8\u05d5\u05d5\u05d7\u05d9\u05dd","\u05d7\u05dc\u05d1\u05d5\u05df: 9\u201311% | \u05e9\u05d5\u05de\u05df: 4\u20137% | \u05e1\u05d5\u05db\u05e8: 16\u201320% | \u05de\u05d9\u05dd: 62\u201368%"],
            ["\u05e1\u05d5\u05db\u05e8 \u05d7\u05db\u05dd","\u05e2\u05d3\u05d9\u05e3 \u05dc\u05e9\u05dc\u05d1 \u05d7\u05dc\u05e7 \u05de\u05d4\u05e1\u05d5\u05db\u05e8 \u05db\u05e1\u05d9\u05e8\u05d5\u05e4 (\u05d3\u05d1\u05e9 / \u05d2\u05dc\u05d5\u05e7\u05d5\u05d6 / \u05e1\u05d9\u05dc\u05d0\u05df) \u05e2\u05dd \u05e1\u05d5\u05db\u05e8 \u05e8\u05d2\u05d9\u05dc. \u05de\u05d3\u05d5\u05e2? \u05e1\u05d9\u05e8\u05d5\u05e4 \u05de\u05d5\u05e8\u05d9\u05d3 \u05e0\u05e7\u05d5\u05d3\u05ea \u05e7\u05d9\u05e4\u05d0\u05d5\u05df \u05d9\u05d5\u05ea\u05e8 \u05d5\u05e0\u05d5\u05ea\u05df \u05de\u05e8\u05e7\u05dd \u05d7\u05dc\u05e7 \u05d9\u05d5\u05ea\u05e8"],
            ["\u05d0\u05d1\u05e7\u05ea \u05d7\u05dc\u05d1\u05d5\u05df","\u05db\u05dc \u05de\u05d5\u05ea\u05d2 \u05e9\u05d5\u05e0\u05d4! \u05d4\u05db\u05e0\u05e1 \u05d3\u05e8\u05da '\u05de\u05e8\u05db\u05d9\u05d1 \u05de\u05e9\u05dc\u05d9' \u05e2\u05dd \u05d4\u05e2\u05e8\u05db\u05d9\u05dd \u05de\u05d4\u05ea\u05d5\u05d5\u05d9\u05ea \u05e9\u05dc\u05da \u05dc-100\u05d2\u05e8'. \u05d4\u05de\u05d9\u05dd \u05d9\u05d7\u05d5\u05e9\u05d1\u05d5 \u05d0\u05d5\u05d8\u05d5\u05de\u05d8\u05d9\u05ea. Whey Isolate \u05e620% \u05d7\u05dc\u05d1\u05d5\u05df"],
            ["\u05d1\u05e1\u05d9\u05e1 \u05de\u05d5\u05de\u05dc\u05e5","\u05d9\u05d5\u05d2\u05d5\u05e8\u05d8 \u05d9\u05d5\u05d5\u05e0\u05d9 0% + \u05d7\u05dc\u05d1 3% + \u05d0\u05d1\u05e7\u05ea \u05d7\u05dc\u05d1\u05d5\u05df. \u05e7\u05d5\u05d8\u05d2' \u05e0\u05d5\u05ea\u05df \u05de\u05e8\u05e7\u05dd \u05e7\u05e8\u05de\u05d9 \u05de\u05e6\u05d5\u05d9\u05df"],
            ["\u05d0\u05d6\u05d4\u05e8\u05d4","\u05d9\u05d5\u05ea\u05e8 \u05de\u05d3\u05d9 \u05d0\u05d1\u05e7\u05ea \u05d7\u05dc\u05d1\u05d5\u05df = \u05d2\u05dc\u05d9\u05d3\u05d4 \u05d2\u05e8\u05d2\u05e8\u05d9\u05ea \u05d5\u05e7\u05e9\u05d4. \u05de\u05e7\u05e1\u05d9\u05de\u05d5\u05dd 30\u201340\u05d2\u05e8' \u05dc-500\u05de\"\u05dc"],
          ].map(([t,d])=>(<div key={t} style={{display:"flex",gap:8,marginBottom:8,fontSize:13}}>
            <span style={{background:"#E8F4FF",color:"#0EA5E9",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,whiteSpace:"nowrap",height:"fit-content",border:"1px solid #BAE6FD"}}>{t}</span>
            <span style={{color:"#555",lineHeight:1.5}}>{d}</span>
          </div>))}
        </div>
        {/* אלכוהול */}
        <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontWeight:800,fontSize:14,marginBottom:12,color:"#1a1a2e",display:"flex",alignItems:"center",gap:8}}>🍸 גלידות אלכוהוליות</div>
          {[
            ["📐 הנוסחה","כמות משקה = (נפח תערובת × 0.015) ÷ (ABV÷100). יעד: 1.5–2% אתנול אמיתי בתערובת"],
            ["📊 טבלה","וודקה/רום/עראק 40%: 19מ\"ל (500) / 28מ\"ל (750). ליקר 20%: 38מ\"ל (500) / 56מ\"ל (750). ביילי'ס 17%: 44מ\"ל (500) / 66מ\"ל (750)"],
            ["⚠️ גבול","מעל 2% אתנול → הגלידה לא תתקשה במקפיא ביתי (-18°C)"],
            ["✅ הדרך הנכונה","תמיד לשים את האלכוהול בתערובת לפני ההקפאה. לחיזוק טעם בלבד: 3–7מ\"ל (כוס 500) / 5–10מ\"ל (כוס 750) בסיבוב השני (Mix-in) – לא לעשות גם Mix-in וגם Re-spin"],
            ["🎭 ליקר","עדיף ליקר 20–30% על פני חריף 40% – קל יותר לאיזון וארומטי יותר"],
            ["🧂 סוד השף","קורט מלח (1גר' ל-500מ\"ל) מחזק את הטעם האלכוהולי ומשפר קינוח"],
          ].map(([t,d])=>(<div key={t} style={{display:"flex",gap:8,marginBottom:8,fontSize:13}}>
            <span style={{background:"#F0D6FF",color:"#7C3AED",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,whiteSpace:"nowrap",height:"fit-content",border:"1px solid #DDD6FE"}}>{t}</span>
            <span style={{color:"#555",lineHeight:1.5}}>{d}</span>
          </div>))}
          {/* הערה מיוחדת - סורבה אלכוהולי */}
          <div style={{marginTop:10,padding:"10px 14px",borderRadius:12,background:"linear-gradient(135deg,#F0D6FF,#EDE9FE)",border:"1px solid #DDD6FE",display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{fontSize:18,flexShrink:0}}>{"💡"}</span>
            <div>
              <div style={{fontWeight:800,fontSize:12,color:"#7C3AED",marginBottom:3}}>טיפ: גלידת קוקטייל כסורבה</div>
              <div style={{fontSize:12,color:"#6D28D9",lineHeight:1.6}}>רוצה גלידה אלכוהולית קלה וקיצית? אפשר להשתמש בנוסחת הסורבה – החליפי את הבסיס החלבי בפרי + מיץ + ממתיק, והוסיפי את האלכוהול כרגיל. מרגריטה טבעית, פינה קולדה טרופית, או סורבה ליים רום. יצא מרענן ומדויק יותר מגלידת קוקטייל קלאסית.</div>
            </div>
          </div>
        </div>

        {/* מאקרואים */}
        <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontWeight:800,fontSize:14,marginBottom:12,color:"#1a1a2e"}}>📊 מאקרואים</div>
          {[
            ["🧈 שומן","8–14% לגלידה חלבית חלקה. פחות = גרגרי. יותר מ-16% = שמנת כבדה"],
            ["🍬 סוכר","15–22% מוריד נקודת קפיאה. פחות מ-12% = קשה מדי"],
            ["💧 מים","55–70%. עם פרי – עד 78% בסדר. יותר מדי = קרח"],
            ["🍓 פרי","הסוכר והמים מחושבים אוטומטית לפי הפרי האמיתי (בננה=23% סוכר, תות=7%)"],
          ].map(([t,d])=>(<div key={t} style={{display:"flex",gap:8,marginBottom:8,fontSize:13}}>
            <span style={{background:"#1a1a2e",color:"#D4A855",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,whiteSpace:"nowrap",height:"fit-content"}}>{t}</span>
            <span style={{color:"#555",lineHeight:1.5}}>{d}</span>
          </div>))}
        </div>

        {/* תהליך */}
        <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontWeight:800,fontSize:14,marginBottom:12,color:"#1a1a2e"}}>⏰ תהליך</div>
          {[
            ["⏰ הקפאה","24–48 שעות לפחות. הקפא לגמרי לפני עיבוד"],
            ["🔄 Re-spin","לא חלק? הוסף כף נוזל (חלב/מים/שמנת) ולחץ Re-spin"],
            ["❄️ אחסון","כסי בניילון נצמד ישירות על הגלידה למניעת קרח"],
            ["📏 נפח","מלא בין 80%–100% מהכוס. כוס ריקה מדי = Ninja לא יעבד טוב"],
          ].map(([t,d])=>(<div key={t} style={{display:"flex",gap:8,marginBottom:8,fontSize:13}}>
            <span style={{background:"#EFF6FF",color:"#2563EB",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,whiteSpace:"nowrap",height:"fit-content",border:"1px solid #BFDBFE"}}>{t}</span>
            <span style={{color:"#555",lineHeight:1.5}}>{d}</span>
          </div>))}
        </div>

        {/* טיפים מיוחדים */}
        <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:20,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontWeight:800,fontSize:14,marginBottom:12,color:"#1a1a2e"}}>🌟 טיפים מיוחדים</div>
          {[
            ["🥛 שמנת","לא לבלנדר! תערבבי בעדינות – בלנדר הופך שמנת לחמאה"],
            ["🍋 סורבה","כפית מיץ לימון + כפית דבש = טעם עמוק ומרקם טוב יותר"],
            ["🍌 בננה","בננה בשלה קפואה = מרקם קרמי טבעי ללא שמנת"],
            ["🫐 אסאי","אסאי טהור מר מאוד – חובה להוסיף ממתיק"],
            ["🔬 אלולוז","ממתיק 0 קלוריות – הוסף 30% יותר ממינון סוכר רגיל"],
            ["⅛ קסנטן","⅛ כפית קסנטן גאם = מרקם חלק במיוחד לגלידות טבעוניות"],
            ["🥛 חלב מרוכז","ערבבי היטב לפני הקפאה – לא לקפוא ישירות מהפחית"],
          ].map(([t,d])=>(<div key={t} style={{display:"flex",gap:8,marginBottom:8,fontSize:13}}>
            <span style={{background:"#fff0f0",color:"#E8394D",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,whiteSpace:"nowrap",height:"fit-content",border:"1px solid #fca5a5"}}>{t}</span>
            <span style={{color:"#555",lineHeight:1.5}}>{d}</span>
          </div>))}
        </div>
      </div>)}

      {/* Community Tab */}
      {tab === "community" && (
        <div style={{ padding: "0 4px" }}>
          <div style={{ marginBottom: 20, textAlign: "center" }}>
            <h2 style={{ fontSize: 22, fontWeight: 900, color: "#7C3AED" }}>מתכוני הקהילה 🌍</h2>
            <p style={{ fontSize: 14, color: "#666" }}>גלו שילובים חדשים ששותפו על ידי הקהילה</p>
          </div>

          {allRecipes.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#888", background: "#fff", borderRadius: 18 }}>
              <p>עדיין אין מתכונים בקהילה... תהיו הראשונים לשתף! 🍦</p>
            </div>
          ) : (
           allRecipes.map((r) => {
  const ti = ICE_CREAM_TYPES.find(t => t.id === r.type) || { label: "מעורב 🎨", accent: "#888" };
  const allAvail = getAllIngredients(customIngs);
  const isOpen = openRecipeId === r.id;
  return (
    <div key={r.id} style={{ background: "#fff", borderRadius: 18, marginBottom: 14, boxShadow: "0 4px 16px rgba(124,58,237,0.1)", border: "1px solid #f3e8ff", overflow: "hidden" }}>
      <div onClick={() => setOpenRecipeId(isOpen ? null : r.id)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", cursor: "pointer" }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#1a1a2e" }}>{r.title}</div>
          <div style={{ fontSize: 12, color: "#7C3AED", fontWeight: 600, marginTop: 2 }}>{ti.label}</div>
        </div>
        <span style={{ fontSize: 18, color: "#7C3AED" }}>{isOpen ? "▲" : "▼"}</span>
      </div>
      {isOpen && (
        <div style={{ padding: "0 16px 16px" }}>
          {r.ingredients && Object.keys(r.ingredients).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {Object.entries(r.ingredients).map(([id, amt]) => {
                const ing = allAvail.find(i => i.id === id);
                if (!ing || !amt) return null;
                return (
                  <div key={id} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13 }}>
                    <span style={{ color: "#333", fontWeight: 500 }}>{ing.name}</span>
                    <span style={{ fontWeight: 700, color: "#1a1a2e" }}>{amt} <span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>{ing.unit}</span></span>
                  </div>
                );
              })}
            </div>
          )}
          {r.notes && <div style={{ fontSize: 13, color: "#666", marginBottom: 12, padding: "8px 12px", background: "#f9f5ff", borderRadius: 10 }}>📝 {r.notes}</div>}
          <button onClick={() => { setAmounts(r.ingredients || {}); setRecipeName(r.title); setTab("calc"); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ width: "100%", padding: "9px", borderRadius: 10, border: "none", cursor: "pointer", background: "linear-gradient(120deg,#D4A855,#c08a30)", color: "#1a1a2e", fontWeight: 800, fontSize: 13, fontFamily: "inherit" }}>⚡ בחר מתכון זה</button>
        </div>
      )}
    </div>
  );
})
          )}
        </div>
      )}

      {/* Calc Tab */}
      {tab==="calc"&&(<>
        {/* כפתורים */}
        <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
          <button onClick={handleAutoBalance} disabled={activeCt===0} style={{flex:1,minWidth:120,padding:"10px 12px",borderRadius:12,border:"none",cursor:activeCt>0?"pointer":"not-allowed",fontFamily:"inherit",background:activeCt>0?"linear-gradient(120deg,#0f3460,#1a1a2e)":"#ddd",color:activeCt>0?"#D4A855":"#aaa",fontWeight:800,fontSize:13,boxShadow:activeCt>0?"0 3px 10px rgba(0,0,0,0.2)":"none"}}>⚡ איזון אוטומטי</button>
          <button onClick={()=>{if(macros)setShowSave(true);}} disabled={!macros} style={{padding:"10px 12px",borderRadius:12,border:"none",cursor:macros?"pointer":"not-allowed",fontFamily:"inherit",background:macros?"#2D8A4E":"#ddd",color:macros?"#fff":"#aaa",fontWeight:700,fontSize:13}}>💾 שמור</button>
         <button onClick={()=>{if(window.confirm("\u05d1\u05d8\u05d5\u05d7\u05d4? \u05db\u05dc \u05d4\u05de\u05e8\u05db\u05d9\u05d1\u05d9\u05dd \u05d9\u05d9\u05de\u05d7\u05e7\u05d5")){setAmounts({});setTypeOverride(null);}}} style={{padding:"10px 16px",borderRadius:12,border:"2px solid #E8394D",cursor:"pointer",fontFamily:"inherit",background:"#fee2e2",color:"#E8394D",fontWeight:800,fontSize:14}}>{"🔄 \u05d0\u05e4\u05e1 \u05d4\u05db\u05dc"}</button>
        </div>

        {showSave&&(<div style={{background:"#fff",borderRadius:14,padding:14,marginBottom:14,boxShadow:"0 4px 16px rgba(0,0,0,0.09)",border:"2px solid #2D8A4E"}}>
          <div style={{fontWeight:800,marginBottom:8,fontSize:14}}>💾 שמור מתכון</div>
          <div style={{display:"flex",gap:8}}>
            <input value={recipeName} onChange={e=>setRecipeName(e.target.value)} placeholder="שם המתכון..." onKeyDown={e=>e.key==="Enter"&&handleSave()} style={{flex:1,padding:"8px 12px",borderRadius:10,border:"1.5px solid #ddd",fontSize:14,fontFamily:"inherit",outline:"none"}}/>
            <button onClick={saveToCloud} style={{padding:"8px 14px",borderRadius:10,border:"none",cursor:"pointer",background:"#2D8A4E",color:"#fff",fontWeight:700,fontFamily:"inherit"}}>שמור</button>
            <button onClick={()=>setShowSave(false)} style={{padding:"8px 10px",borderRadius:10,border:"none",cursor:"pointer",background:"#fee2e2",color:"#E8394D",fontWeight:700,fontFamily:"inherit"}}>✕</button>
          </div>
          <textarea value={recipeNote} onChange={e=>setRecipeNote(e.target.value)} placeholder="הערות אישיות (אופציונלי)... למשל: הוסיפי תמצית וניל, יצא מצוין עם בננה בשלה..." rows={2} style={{width:"100%",marginTop:8,padding:"8px 12px",borderRadius:10,border:"1.5px solid #ddd",fontSize:13,fontFamily:"inherit",outline:"none",resize:"vertical",boxSizing:"border-box",color:"#555"}}/>
        </div>)}

        <CupMeter filled={totalW} container={container}/>

        {macros?(<div style={{background:"#fff",borderRadius:18,padding:"16px",marginBottom:14,boxShadow:"0 4px 18px rgba(0,0,0,0.08)",border:"1.5px solid #f0e0c0"}}>
          <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
            <ScoreRing score={score}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:14,marginBottom:10}}>📊 ניתוח</div>
              <MacroBar label="שומן"  value={macros.fat}   min={target.fat[0]}   max={target.fat[1]}   color="#D4A855"/>
              <MacroBar label="סוכר"  value={macros.sugar} min={target.sugar[0]} max={target.sugar[1]} color="#E8394D"/>
              <MacroBar label="% מים" value={macros.water} min={target.water[0]} max={target.water[1]} color="#2563EB"/>
              <MacroBar label="💪 חלבון" value={macros.protein} min={target.protein?target.protein[0]:2} max={target.protein?target.protein[1]:6} color="#0EA5E9"/>
              {(()=>{
                // שורת אתנול – מוצגת רק אם יש אלכוהול
                const allI=getAllIngredients(customIngs);
                let ethanolMl=0;
                for(const id of ALCOHOL_IDS){ const amt=amounts[id]||0; if(!amt) continue; const ing=allI.find(i=>i.id===id); if(ing?.abv) ethanolMl+=amt*(ing.abv/100); }
                for(const ing of customIngs){ if(ing.abv&&(amounts[ing.id]||0)>0) ethanolMl+=amounts[ing.id]*(ing.abv/100); }
                if(ethanolMl===0) return null;
                const ethanolPct=macros.weight>0?ethanolMl/macros.weight*100:0;
                const ok=ethanolPct<=1.5;
                const warn=ethanolPct>1.5&&ethanolPct<=2;
                return(<div style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:3,fontWeight:600}}>
                    <span style={{color:"#444"}}>🍸 אתנול</span>
                    <span style={{color:ok?"#2D8A4E":warn?"#F59E0B":"#E8394D"}}>{ethanolPct.toFixed(2)}%<span style={{color:"#aaa",fontWeight:400}}> (יעד: 1.5–2%)</span></span>
                  </div>
                  <div style={{background:"#eee",borderRadius:8,height:10,overflow:"hidden",position:"relative"}}>
                    <div style={{position:"absolute",left:"1.5%",width:"0.5%",background:"rgba(45,138,78,0.2)",height:"100%"}}/>
                    <div style={{width:`${Math.min(ethanolPct/4*100,100)}%`,background:ok?"#7C3AED":warn?"#F59E0B":"#E8394D",height:"100%",borderRadius:8,transition:"width 0.4s"}}/>
                  </div>
                </div>);
              })()}
              <div style={{fontSize:11,color:"#ccc",textAlign:"center"}}>חלבון: {macros.protein.toFixed(1)}%</div>
            </div>
          </div>
        </div>):(<div style={{background:"rgba(255,255,255,0.5)",borderRadius:14,padding:"18px",marginBottom:14,textAlign:"center",color:"#aaa",fontSize:13}}>הוסף מרכיבים כדי לראות את הניתוח 👆</div>)}

{/* סוג גלידה – זיהוי אוטומטי בתחתית */}
        {activeCt>0&&(()=>{
          const typeInfo = ICE_CREAM_TYPES.find(t=>t.id===detectedType)||ICE_CREAM_TYPES[0];
          return(
            <div style={{background:`linear-gradient(135deg,${typeInfo.color},#fff)`,borderRadius:14,padding:"12px 16px",marginBottom:14,border:`2px solid ${typeInfo.accent}`,display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1}}>
                <div style={{fontSize:11,color:"#888",marginBottom:2}}>סוג גלידה שזוהה אוטומטית</div>
                <div style={{fontWeight:900,fontSize:16,color:typeInfo.accent}}>{typeInfo.label}</div>
              </div>
              <div style={{width:40,height:40,borderRadius:"50%",background:typeInfo.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{"🎯"}</div>
            </div>
          );
        })()}
        {/* Override ידני לסוג הגלידה */}
        {activeCt>0&&(
          <div style={{background:"#fff",borderRadius:14,padding:"12px 16px",marginBottom:14,boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"1.5px solid #e0e0e0"}}>
            <div style={{fontSize:12,color:"#888",marginBottom:8}}>🎛️ רוצה לעקוף את הזיהוי האוטומטי?</div>
            <select
              value={typeOverride||""}
              onChange={e=>setTypeOverride(e.target.value||null)}
              style={{width:"100%",padding:"8px 12px",borderRadius:10,border:"1.5px solid #ddd",fontSize:13,fontFamily:"inherit",background:"#fafafa",color:"#333",outline:"none"}}
            >
              <option value="">🤖 זיהוי אוטומטי ({typeInfo.label})</option>
              <option value="heavyCream">🍦 גלידת שמנת קלאסית</option>
              <option value="dairy">🥛 גלידה חלבית קלה / ג'לאטו</option>
              <option value="coconut">🥥 קוקוס</option>
              <option value="fruitCoconut">🥥🍓 קוקוס + פרי</option>
              <option value="sorbet">🍧 סורבה</option>
              <option value="protein">💪 גלידת חלבון</option>
              <option value="vegan">🌱 טבעוני</option>
              <option value="mixed">🎨 מעורב</option>
            </select>
            {typeOverride&&(
              <button onClick={()=>setTypeOverride(null)} style={{marginTop:8,padding:"4px 12px",borderRadius:8,border:"none",cursor:"pointer",background:"#fee2e2",color:"#E8394D",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>
                ✕ חזור לזיהוי אוטומטי
              </button>
            )}
          </div>
        )}

{macros && (amounts["coconutOil"]||0) > 0 && (()=>{
         const coilPct = (amounts["coconutOil"]) / macros.weight * 100;
         if(coilPct <= COCONUT_OIL_MAX_PCT) return null;
         return(
           <div style={{background:"#fff8e1",border:"2px solid #E8394D",borderRadius:12,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#333",lineHeight:1.5}}>
             ⚠️ <strong>שמן קוקוס טהור {coilPct.toFixed(1)}%</strong> – קופא כחלקיקי שעווה ויוצר מרקם גרגירי! החלף בקרם קוקוס מפחית (17–22% שומן).
           </div>
         );
       })()}
        {macros && (amounts["instantPudding"]||0) > 0 && (()=>{
          const pu = amounts["instantPudding"];
          const hasCoconut = (amounts["coconutMilk"]||0)+(amounts["coconutCream"]||0)>0;
          let msg = null;
          if(hasCoconut)
            msg = "🥥🍮 שילוב קוקוס + פודינג עלול לייצר מרקם צמיגי/כבד. מומלץ להפחית באחד מהם.";
          else if(detectedType==="sorbet" && pu>10)
            msg = "🍧 עודף פודינג בסורבה – יהפוך אותו לג'ל מלאכותי. עד 10גר' בלבד.";
          else if(detectedType==="vegan")
            msg = "🌱⚠️ אינסטנט פודינג רגיל מכיל מוצקי חלב – הופך את המתכון ללא טבעוני!";
          else if(pu>15)
            msg = `⚠️ פודינג ${pu}גר' – כמות גבוהה מדי. יעד: 5–15גר'.`;
          if(!msg) return null;
          return(
            <div style={{background:"#fff8e1",border:"2px solid #F59E0B",borderRadius:12,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#333",lineHeight:1.5}}>
              {msg}
            </div>
          );
        })()}

        {totalW===0&&(<div style={{background:"linear-gradient(120deg,#1a1a2e,#0f3460)",color:"#a0b4cc",borderRadius:12,padding:"9px 13px",marginBottom:14,fontSize:12}}>
          <strong style={{color:"#fff"}}>📏 {container.icon} {container.label}:</strong> מלא {container.minFill}–{container.maxFill}גר'. הקפא 24 שעות.
        </div>)}

        {/* קטגוריות */}
        {Object.entries(INGREDIENTS).map(([cat,ings])=>{
          const active=ings.filter(i=>(amounts[i.id]||0)>0).length;
          return(<div key={cat} style={{marginBottom:10}}>
            <button onClick={()=>toggleCat(cat)} style={{width:"100%",textAlign:"right",background:expanded[cat]?"#1a1a2e":"#fff",color:expanded[cat]?"#fff":"#333",border:"none",borderRadius:expanded[cat]?"14px 14px 0 0":"14px",padding:"11px 14px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit",display:"flex",justifyContent:"space-between",alignItems:"center",boxShadow:"0 2px 6px rgba(0,0,0,0.06)",transition:"all 0.15s"}}>
              <span>{CATEGORY_LABELS[cat]}</span>
              <span style={{display:"flex",alignItems:"center",gap:8}}>{active>0&&<span style={{background:"#D4A855",color:"#1a1a2e",borderRadius:10,padding:"1px 8px",fontSize:11,fontWeight:800}}>{active}</span>}<span>{expanded[cat]?"▲":"▼"}</span></span>
            </button>
            {expanded[cat]&&(<div style={{background:"rgba(255,255,255,0.6)",borderRadius:"0 0 14px 14px",padding:"8px 6px 4px"}}>
              {ings.map(ing=><IngredientRow key={ing.id} ing={ing} amount={amounts[ing.id]||0} onChange={handleAmount}/>)}
            </div>)}
          </div>);
        })}

        {/* מרכיב מותאם */}
        <div style={{marginBottom:10}}>
          <button onClick={()=>toggleCat("_custom")} style={{width:"100%",textAlign:"right",background:expanded["_custom"]?"#7C3AED":"#fff",color:expanded["_custom"]?"#fff":"#7C3AED",border:`2px dashed ${expanded["_custom"]?"transparent":"#7C3AED"}`,borderRadius:expanded["_custom"]?"14px 14px 0 0":"14px",padding:"11px 14px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit",display:"flex",justifyContent:"space-between",alignItems:"center",boxShadow:"0 2px 6px rgba(0,0,0,0.06)"}}>
            <span>🧪 מרכיב משלי {customIngs.length>0&&`(${customIngs.length})`}</span>
            <span style={{display:"flex",alignItems:"center",gap:8}}>
              {customIngs.filter(i=>(amounts[i.id]||0)>0).length>0&&<span style={{background:"#D4A855",color:"#1a1a2e",borderRadius:10,padding:"1px 8px",fontSize:11,fontWeight:800}}>{customIngs.filter(i=>(amounts[i.id]||0)>0).length}</span>}
              <span>{expanded["_custom"]?"▲":"▼"}</span>
            </span>
          </button>
          {expanded["_custom"]&&(<div style={{background:"rgba(237,233,254,0.4)",borderRadius:"0 0 14px 14px",padding:"8px 6px 6px",border:"2px dashed #7C3AED",borderTop:"none"}}>
           {customIngs.map((ing, index) => (
  <IngredientRow 
  key={ing.id || index}
  ing={ing}
  amount={amounts[ing.id] || 0}
 onChange={(id, val) => setAmounts(prev => ({ ...prev, [id]: val }))}
  onRemove={() => deleteIngredient(index)} // שינינו את זה מ-onRemove פשוט ל-deleteIngredient
/>
))}
            <InlineCustomForm onAdd={ing=>setCustomIngsAndSave(prev=>[...prev,ing])} container={container}/>
          </div>)}
        </div>

        {/* טיפים חכמים לפי מרכיבים */}
        {(()=>{
          const smartTips = [];
          if((amounts["cream38"]||0)>0)
            smartTips.push({icon:"🥛", text:"שמנת מתוקה – אל תכניסי לבלנדר! תערבבי בעדינות. בלנדר הופך שמנת לחמאה"});
          if((amounts["condensedMilk"]||0)>0)
            smartTips.push({icon:"🥛", text:"חלב מרוכז – ערבבי היטב לפני הקפאה, אל תקפאי ישירות מהפחית"});
          if(detectedType==="sorbet"||detectedType==="fruitCoconut"||(amounts["strawberry"]||amounts["mango"]||amounts["acaiFrozen"])>0)
            smartTips.push({icon:"🍋", text:"סורבה ופרי – כפית מיץ לימון + כפית דבש מעמיקים את הטעם ומשפרים מרקם"});
          if((amounts["banana"]||0)>0)
            smartTips.push({icon:"🍌", text:"בננה – עדיף להקפיא בשלה. נותנת מרקם קרמי טבעי"});
          if((amounts["acaiFrozen"]||0)>0)
            smartTips.push({icon:"🫐", text:"אסאי טהור מר מאוד – חובה להוסיף 20–30גר' ממתיק"});
          if(ALCOHOL_IDS.some(id=>(amounts[id]||0)>0))
            smartTips.push({icon:"🍸", text:"אלכוהול – יעד: 1.5–2% אתנול. לחיזוק טעם: הוסיפי 3–7מ\"ל (500מ\"ל) / 5–10מ\"ל (750מ\"ל) ב-Mix-in בלבד. קורט מלח מחזק טעם!"});
          if((amounts["coconutOil"]||0)>0){
            const coilPct = macros ? (amounts["coconutOil"]/macros.weight*100) : 0;
            if(coilPct > COCONUT_OIL_MAX_PCT)
              smartTips.push({icon:"⚠️", text:`שמן קוקוס טהור ${coilPct.toFixed(1)}% – קופא כחלקיקי שעווה ויוצר מרקם גרגירי! החלף בקרם קוקוס מפחית.`});
            else
              smartTips.push({icon:"🥥", text:"שמן קוקוס – כמות קטנה בלבד. עדיף להחליף בקרם קוקוס מפחית למרקם חלק."});
          }
          if((amounts["instantPudding"]||0)>0){
            const pu = amounts["instantPudding"];
            const hasCoconut = (amounts["coconutMilk"]||0)+(amounts["coconutCream"]||0)>0;
            if(hasCoconut)
              smartTips.push({icon:"🥥", text:"שילוב קוקוס + פודינג עלול לייצר מרקם צמיגי/כבד. מומלץ להפחית באחד מהם."});
            else if(detectedType==="sorbet"&&pu>10)
              smartTips.push({icon:"🍧", text:"עודף פודינג בסורבה – יהפוך אותו לג'ל מלאכותי. עד 10גר' בלבד."});
            else if(detectedType==="vegan")
              smartTips.push({icon:"⚠️", text:"אינסטנט פודינג רגיל מכיל מוצקי חלב – הופך את המתכון ללא טבעוני!"});
            else if(pu>15)
              smartTips.push({icon:"⚠️", text:`פודינג ${pu}גר' – כמות גבוהה מדי. יעד: 5–15גר'.`});
            else if(pu>=5)
              smartTips.push({icon:"🍮", text:`אינסטנט פודינג ${pu}גר' – טווח אופטימלי ✅`});
          }
          return smartTips.length>0?(
            <div style={{marginBottom:14}}>
              {smartTips.map((tip,i)=>(
                <div key={i} style={{display:"flex",gap:10,padding:"9px 12px",borderRadius:10,marginBottom:6,background:"#EFF6FF",border:"1px solid #BFDBFE",fontSize:12,lineHeight:1.5}}>
                  <span style={{fontSize:16,flexShrink:0}}>{tip.icon}</span>
                  <span style={{color:"#1e40af"}}>{tip.text}</span>
                </div>
              ))}
            </div>
          ):null;
        })()}

        

        <button onClick={()=>setTab("tips")} style={{width:"100%",padding:"11px",borderRadius:12,border:"1.5px solid #e0e0e0",cursor:"pointer",background:"#fff",color:"#555",fontWeight:600,fontSize:13,fontFamily:"inherit",marginBottom:20,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          {"💡 מדריך טיפים מלא ←"}
        </button>
        <div style={{textAlign:"center",fontSize:11,color:"#ccc",paddingBottom:30}}>Ninja Creami Calculator v4</div>
      </>)}
    </div>
    <footer style={{
  marginTop: '40px',
  padding: '20px',
  borderTop: '1px solid #eee',
  textAlign: 'center',
  fontSize: '14px',
  color: '#666'
}}>
  <p>נתקלת בבעיה? יש לך שאלה?</p>
  <a href="mailto:Yeshliglidasup@gmail.com" style={{ 
    color: '#007bff', 
    textDecoration: 'none',
    fontWeight: 'bold',
    display: 'inline-block',
    marginTop: '5px'
  }}>
    📧 שלחו לנו מייל: Yeshliglidasup@gmail.com
  </a>
</footer>
  </div>);
}